<template>
  <div class="mt-4 bg-white border border-indigo-200 rounded shadow">

    <!-- 見出し（クリックで開閉） -->
    <div
      class="flex items-center cursor-pointer select-none text-sm font-bold text-indigo-900 bg-indigo-50 hover:bg-indigo-100 rounded-t px-3 py-2 transition-colors"
      :class="opened ? 'border-b border-indigo-200' : ''"
      @click="opened = !opened"
    >
      <span class="mr-2 text-xs">{{ opened ? '▼' : '▶' }}</span>
      雇用指示一覧・編集
      <span class="ml-3 text-xs font-normal text-gray-500">{{ value.length }}件</span>
      <!-- 開いていなくても直した箇所が分かるようにする（承認者の確認用） -->
      <span v-if="changedCount" class="changed-mark">（{{ changedCount }}箇所変更）</span>
      <span v-if="addedCount" class="changed-mark">（追加{{ addedCount }}件）</span>
      <!-- 取り消した雇用指示（無かった扱いにする）は入力欄が変わらないので変更の数に入らない。承認者が見落とさないよう別に出す -->
      <span v-if="cancelledCount" class="changed-mark cancel">（取消{{ cancelledCount }}件）</span>
    </div>

    <div class="accordion" :class="opened ? 'open' : ''">
      <div class="flex font-bold items-center mt-2 mx-2 border-2 rounded border-gray-300">
        <!-- 清掃員名 -->
        <div class="pl-3">
          <div class="text-indigo-900">{{ name }}</div>
          <div class="text-sm text-indigo-900 text-center">{{ staffDailyCleaning }}</div>
        </div>

        <!-- 終わった雇用指示も出すチェック（入社・再入社では出さない。承認待ち（承認のとき）は出すが押せない。親の showEndedToggle・endedToggleDisabled）
             退職の取り消し・延期や、間違えて作業終了にした行を呼び戻して「修正（上書き）」で直すためのもの。取得・追加は親（toggleEnded） -->
        <label v-if="showEndedToggle" class="ended-toggle" :class="endedToggleDisabled ? 'disabled' : ''" v-tooltip="endedToggleDisabled ? '承認待ちの申請では変えられません。' : '終わった雇用指示も表に出します。退職の取り消し・延期や、間違えて作業終了にした行を「修正（上書き）」で直せます。'">
          <input type="checkbox" :checked="showEnded" :disabled="endedToggleDisabled" @change="$emit('update:showEnded', $event.target.checked)" />
          終了した雇用指示も表示
        </label>

        <!-- 週合計時間 -->
        <div class="ml-auto p-3 bg-gray-100 text-center">
          <div class="text-xs text-gray-600">週合計</div>
          <div class="mt-2 text-green-700">{{ totalHours }} h</div>
        </div>

        <!-- 月額 -->
        <div class="border-l border-gray-300 p-3 bg-gray-100 text-center">
          <div class="text-xs text-gray-600">月額</div>
          <div class="mt-2 text-green-700">{{ totalSalary }} 円</div>
        </div>
      </div>

      <div class="ml-auto mb-1 px-2 w-fit text-xs font-bold text-gray-500">※週合計・月額は作業終了が入っていない物件のみ計算（書類手続きに必要）</div>

      <!-- 表の操作。物件追加は表の下だと気づかれないため、左上に置く -->
      <div class="toolbar px-2 mb-1">
        <PrimaryButton text="物件を追加" size="xs" @click="addRow">
          <Icon slot="before" iconName="Plus" iconType="outline" :strokeWidth="2" class="h-4 w-4" />
        </PrimaryButton>

        <!-- 契約詳細の行（紫）の表示切替。行と同じ紫にして、何が切り替わるか分かるようにする -->
        <label class="toggle-item contract-toggle" v-tooltip="'契約詳細の行の表示を切り替えます'">
          <Toggle v-model="showContract" size="small" color="purple" />
          <span>契約詳細表示</span>
        </label>
      </div>

      <!-- 縦のスクロールは親画面に任せる（枠を分けると二重スクロールになって操作しづらいため） -->
      <div class="table-scroll px-2">
        <table class="emp-table text-xs">

          <!-- 列幅（th と td はこの指定で揃う） -->
          <colgroup>
            <col class="col-ops" />
            <col class="col-site" />
            <col class="col-term" />
            <col v-for="w in weekList" :key="'col' + w.id" class="col-day" />
            <col class="col-work" />
            <col class="col-attr" />
            <col class="col-attr" />
            <col class="col-attr" />
          </colgroup>

          <thead>
            <tr>
              <th>操作</th>
              <th>物件名</th>
              <th></th>
              <th
                v-for="w in weekList"
                :key="'th' + w.id"
                :class="{ sun: w.id === 'Sunday' }"
              >
                {{ w.name }}
              </th>
              <th class="attr-start">作業内容</th>
              <th class="attr">祝日勤務</th>
              <th class="attr">ゴミ搬出</th>
              <th class="attr">全社祝日出勤</th>
            </tr>
          </thead>

          <tbody>
            <template v-for="(row, index) in value">

              <!-- 雇用指示1件（勤務行＋賃金行＋備考）の見出しの帯。区切りと名札を兼ねる（契約詳細入力画面の契約コードの帯と同じ考え方） -->
              <!-- ほかの雇用指示で変更・追加を始めたら、変更なしの雇用指示はこの帯の1行に畳む（帯を押すと開閉。閲覧のみでも押せるよう button にしない） -->
              <tr :key="'head' + index" class="seq-head" :class="[rowClass(row), canFold(row) ? 'foldable' : '']" @click="toggleFold(row)">
                <td :colspan="weekList.length + 7">
                  <span v-if="canFold(row)" class="seq-head-mark">{{ isFolded(row, index) ? '▶' : '▼' }}</span>
                  <template v-if="row.cdSite">
                    {{ row.site }}<span class="seq-head-code">（{{ row.cdSite }}）</span>
                    <span class="seq-head-con">契約コード <b>{{ row.cdContract || '未選択' }}</b></span>
                    <!-- 得意先名＋支店名（名札の種類や返却物の送付状は得意先で決まるので、行を開いた時点で分かるように。ユーザー指示） -->
                    <span v-if="row.client1 || row.client2" class="seq-head-client">得意先：{{ [row.client1, row.client2].filter(x => x).join(' ') }}</span>
                  </template>
                  <template v-else>物件未選択</template>
                  <span v-if="row.isCancelled" class="seq-head-tag cancelled">取消</span>
                  <span v-else-if="!row.sequenceNumber && !row.isChangedTo" class="seq-head-tag new">新規追加</span>
                  <span v-else-if="row.isChangedTo" class="seq-head-tag change">変更後</span>
                  <span v-else-if="row.isChangedFrom" class="seq-head-tag before">変更前</span>
                  <span v-else-if="row.mode === 'ended'" class="seq-head-tag before">作業終了</span>
                  <span v-else-if="isUntouched(row)" class="seq-head-tag none">変更なし</span>
                  <!-- 契約詳細の申請のお知らせ（契約終了は、作業終了がまだならローズ色で目立たせる。済んだら灰色） -->
                  <template v-if="rowNotice(row)">
                    <span v-if="rowNotice(row).mode === 'end'" class="seq-head-notice" :class="rowNotice(row).todo ? 'todo' : ''">
                      契約終了の申請あり（終了日 {{ formatDate(rowNotice(row).endDate) || '未入力' }}）{{ rowNotice(row).todo ? '→ この雇用指示を「作業終了」にしてください' : '' }}
                    </span>
                    <span v-else class="seq-head-notice">契約詳細の変更の申請あり（{{ rowNotice(row).modeName }}）</span>
                  </template>
                  <span v-if="canFold(row)" class="seq-head-fold">{{ isFolded(row, index) ? '開く' : '閉じる' }}</span>
                </td>
              </tr>

              <template v-if="!isFolded(row, index)">

              <!-- 受注内容・時間差分（見出しの「契約情報」で開閉する）。
                   今回の申請で変更した元の行は履歴なので、今の契約と突き合わせても意味がなく出さない
                   契約情報が取れていない・中身が無い物件も出さない -->
              <template v-if="hasSiteInfo(row.cdSite) && showContract && !row.isChangedFrom">
                <tr
                  :key="'con' + index"
                  class="contract"
                >
                  <!-- 操作（契約コード行とまとめて1セルにする） -->
                  <td class="cap-cell" :rowspan="getSiteInfo(row.cdSite).contract.length + 1">
                    <span class="cap">契約詳細</span>
                    <!-- 対象日（本日と開始日の遅い方）に効いている契約。その下に取得した日時 -->
                    <span class="fetched text-white">{{ formatDate(getSiteInfo(row.cdSite).day) }}<br>時点の契約</span>
                    <span class="fetched text-white">{{ getSiteInfo(row.cdSite).siteShort.date }}<br>取得</span>
                  </td>

                  <!-- 物件名（この行は物件全体＝全契約の合計） -->
                  <td class="head-cell">
                    <span class="cap site">物件全体</span>
                    <div class="site-name">{{ row.site }}</div>
                  </td>

                  <!-- 合計の不足（月。括弧は1週間あたり） -->
                  <td class="center">
                    <div class="lbl font-bold">物件全体の過不足（月）</div>
                    <div class="lbl font-bold" v-if="getSiteInfo(row.cdSite).siteShort.totalM == 0">※過不足ありません</div>
                    <div v-else :class="hoursClass(shortHours(row.cdSite, 'totalM'), getSiteInfo(row.cdSite).siteShort.totalMColor)">
                      <span class="month-main">{{ signedHours(shortHours(row.cdSite, 'totalM')) }} h/月</span>
                      <span class="week-sub">（{{ signedHours(shortHours(row.cdSite, 'total')) }} h/週）</span>
                    </div>
                  </td>

                  <!-- 曜日別の不足（月。括弧は1週間あたり） -->
                  <td v-for="w in weekList" :key="'short' + index + w.id" class="center">
                    <div :class="hoursClass(shortHours(row.cdSite, w.key + 'M'), getSiteInfo(row.cdSite).siteShort[w.key + 'MColor'])">
                      <span class="month-main">{{ signedHours(shortHours(row.cdSite, w.key + 'M')) }} h/月</span>
                      <span class="week-sub" v-if="Number(shortHours(row.cdSite, w.key + 'M'))">（{{ signedHours(shortHours(row.cdSite, w.key)) }} h/週）</span>
                    </div>
                  </td>

                  <!-- 作業内容・祝日勤務・ゴミ搬出・全社祝日出勤（物件単位では持たない） -->
                  <td class="attr-start center">-</td>
                  <td class="attr center">-</td>
                  <td class="attr center">-</td>
                  <td class="attr center">-</td>
                </tr>

                <!-- 契約コードごとの時間差分 -->
                <template v-if="getSiteInfo(row.cdSite).contract.length">
                  <tr
                    v-for="(con, ci) in getSiteInfo(row.cdSite).contract"
                    :key="index + 'con' + ci"
                    class="contract detail"
                  >
                    <!-- 物件名の列（操作の列は「契約」のセルが縦につながっている）
                         字下げして、物件全体の行にぶら下がっていることを示す -->
                    <!-- 右隣の「契約時間 ➡／この契約の過不足 ➡」の見出しはやめて、このセルを2列分にする（ユーザー指示） -->
                    <td class="head-cell indent" colspan="2">
                      <span class="branch"></span><span class="cap sub">契約コード {{ con.cdContract }}</span>
                      <span class="fetched strong">時間指定：{{ flagText(con.specificTime) }}</span>
                    </td>

                    <!-- 曜日別の契約時間・不足（月。括弧は1週間あたり。足りない曜日は、どの週に何時間足りないかの文も） -->
                    <td v-for="w in weekList" :key="index + 'shortCon' + ci + w.id" class="center">
                      <div class="time">{{ contractTime(con, w) }}</div>
                      <div v-if="Number(con[w.key + 'M'])" :class="con[w.key + 'MColor']">
                        <span class="month-main">{{ signedHours(con[w.key + 'M']) }} h/月</span>
                        <span class="week-sub">（{{ signedHours(con[w.key + '2']) }} h/週）</span>
                      </div>
                      <!-- 契約時間がある曜日は、差が無いことも示す（休みの曜日には出さない） -->
                      <div v-else-if="!isEmpty(con[w.key])" class="lbl zero">過不足なし</div>
                      <div v-if="con[shortTextKey(w)]" class="short-text">{{ con[shortTextKey(w)] }}</div>
                    </td>

                    <!-- 作業内容（契約詳細の突き合わせでは持たない）・祝日勤務・ゴミ搬出・全社祝日出勤 -->
                    <td class="attr-start center">-</td>
                    <td class="attr center">{{ selectionName(holidayType, con.holiday) }}</td>
                    <td class="attr center">{{ flagText(con.garbage) }}</td>
                    <td class="attr center">{{ flagText(con.companyHoliday) }}</td>
                  </tr>
                </template>
              </template>

              <!-- 清掃員の雇用指示 -->
              <tr :key="'sched' + index" class="sched" :class="[rowClass(row), isNg(index, 'row') ? 'ng-row' : '']">
                <!-- 操作（新規追加の行は見落とされないよう、セルごと塗りつぶす） -->
                <td class="ops" :class="!row.sequenceNumber && !row.isChangedTo ? 'new' : ''" :rowspan="rowspan(row)">
                  <!-- 契約内容変更の変更元。履歴として残る行なので操作はさせない -->
                  <template v-if="row.isChangedFrom">
                    <span class="state before">
                      変更前
                      <span class="state-sub">（履歴として残る）</span>
                    </span>
                  </template>

                  <!-- 未保存の行（sequenceNumber なし）は最初から入力できる。
                       契約内容変更で足した行は、完全な新規とは意味が違うので分けて示す -->
                  <template v-else-if="!row.sequenceNumber">
                    <template v-if="row.isChangedTo">
                      <span class="state change">変更後</span>
                      <button
                        type="button"
                        class="op-btn cancel"
                        v-tooltip="'契約内容変更をやめて、この行を取り除きます'"
                        @click="remove(index)"
                      >
                        <Icon iconName="X" iconType="outline" :strokeWidth="2" class="w-3.5 h-3.5 mr-1" />
                        取消
                      </button>
                    </template>

                    <template v-else>
                      <span class="state new">新規追加</span>
                      <button
                        type="button"
                        class="op-btn warn"
                        v-tooltip="'この行を取り除きます'"
                        @click="remove(index)"
                      >
                        <Icon iconName="Trash" iconType="outline" :strokeWidth="2" class="w-3.5 h-3.5 mr-1" />
                        削除
                      </button>
                    </template>
                  </template>

                  <!-- この雇用指示を取り消した行（AKBS 登録で本体から取消の表へ移る）。登録前なら戻せる -->
                  <template v-else-if="row.isCancelled">
                    <span class="state cancelled">
                      取消
                      <span class="state-sub">（無かった扱い）</span>
                    </span>
                    <button
                      type="button"
                      class="op-btn cancel"
                      v-tooltip="'取消をやめて、元の雇用指示に戻します'"
                      @click="uncancelRow(row)"
                    >
                      <Icon iconName="X" iconType="outline" :strokeWidth="2" class="w-3.5 h-3.5 mr-1" />
                      取消をやめる
                    </button>
                  </template>

                  <!-- 賃金のみ変更をした行。足した賃金を入力してもらうため、取消以外は出さない -->
                  <template v-else-if="row.mode === 'changeWage'">
                    <span class="state change">賃金のみ変更</span>
                    <button
                      type="button"
                      class="op-btn cancel"
                      v-tooltip="'賃金のみ変更をやめて、足した賃金を取り除きます'"
                      @click="cancelWage(row)"
                    >
                      <Icon iconName="X" iconType="outline" :strokeWidth="2" class="w-3.5 h-3.5 mr-1" />
                      取消
                    </button>
                  </template>

                  <!-- 作業終了にした行。作業終了日を入れてもらうため、取消以外は出さない -->
                  <template v-else-if="row.mode === 'ended'">
                    <span class="state ended">作業終了です</span>
                    <button
                      type="button"
                      class="op-btn cancel"
                      v-tooltip="'作業終了をやめて、元に戻します'"
                      @click="revert(row)"
                    >
                      <Icon iconName="X" iconType="outline" :strokeWidth="2" class="w-3.5 h-3.5 mr-1" />
                      取消
                    </button>
                  </template>

                  <!-- シーケンスがあり、未操作（契約内容変更で足した行は編集中なのでここには来ない） -->
                  <template v-else-if="!isEditable(row)">
                    <!-- 上書き＝保存済みの行を書き換える（履歴は残らないので、ここだけ警告色にして下に余白を空ける） -->
                    <button
                      type="button"
                      class="op-btn stack overwrite"
                      v-tooltip="'今の内容を書き換えます。変更前の内容は残りません。'"
                      @click="edit(row, 'overwrite')"
                    >
                      修正（上書き）
                      <span class="op-sub">（履歴なし）</span>
                    </button>

                    <!-- 契約内容変更 -->
                    <button
                      type="button"
                      class="op-btn stack keep"
                      v-tooltip="'今の契約内容は履歴として残ります。'"
                      @click="edit(row, 'change', index)"
                    >
                      契約内容変更
                      <span class="op-sub">（履歴残す）</span>
                    </button>

                    <!-- 賃金のみ変更 -->
                    <button
                      type="button"
                      class="op-btn stack keep"
                      v-tooltip="'今の賃金は履歴として残ります。'"
                      @click="edit(row, 'changeWage')"
                    >
                      賃金のみ変更
                      <span class="op-sub">（履歴残す）</span>
                    </button>

                    <!-- 作業終了。内容の変更ではないので、上に余白を空けて切り離す -->
                    <button
                      type="button"
                      class="op-btn stack finish"
                      v-tooltip="'この物件の作業を終了します。作業終了日の入力が必要です。'"
                      @click="end(row)"
                    >
                      作業終了
                    </button>

                    <!-- この雇用指示の取消（物件を追加したが1日も働かずに辞めた等）。開始日から 21 日後の当日までだけ出す -->
                    <button
                      v-if="canCancelRow(row)"
                      type="button"
                      class="op-btn stack cancel-row"
                      v-tooltip="'この雇用指示を最初から無かったものにします。開始日から3週間以内だけ押せます。'"
                      @click="cancelRow(row)"
                    >
                      雇用指示を取消
                      <span class="op-sub">（無かった扱い）</span>
                    </button>
                  </template>

                  <!-- シーケンスがあり、修正中 -->
                  <template v-else>
                    <span class="state edit">修正（上書き）</span>
                    <button
                      type="button"
                      class="op-btn cancel"
                      v-tooltip="'編集をやめて、元の内容に戻します'"
                      @click="cancel(row, index)"
                    >
                      <Icon iconName="X" iconType="outline" :strokeWidth="2" class="w-3.5 h-3.5 mr-1" />
                      編集取消
                    </button>
                  </template>
                </td>

                <!-- 物件名 -->
                <td :rowspan="rowspan(row)" class="text-center">
                  <!-- 保存済み（sequenceNumber あり）の行は物件を変更させず表示のみ -->
                  <!-- 未保存の行は物件を選び直せる（「物件を追加」でモーダルから選んで足すので、物件が空の行はできない） -->
                  <!-- 物件が未選択なら枠の中に「物件を選んでください」を出す -->
                  <div v-if="!row.sequenceNumber&&!row.changeFromSequenceNumber" class="px-1.5 py-0.5 select-site whitespace-pre border rounded border-gray-300 cursor-pointer font-bold" :class="row.site ? '' : 'text-red-700'" v-tooltip="'クリックすると物件選択可能'" @click="openSelectSite(row, index)">
                    {{ row.site || '物件を選んでください' }}
                  </div>
                  <div v-else class="font-bold m-1.5 text-indigo-900 whitespace-pre">
                    {{ row.site }}
                  </div>

                  <div class="text-gray-400 text-xs">（{{ row.cdSite }}）</div>

                  <!-- 契約コード（物件を選ぶまで選択肢は無い。保存済み（sequenceNumber あり）の行は変更させず表示のみ） -->
                  <div class="mt-3">
                    <div class="cell-cap">契約コード</div>
                    <select
                      v-if="!row.sequenceNumber"
                      v-model="row.cdContract"
                      class="cell-select text-center"
                      :class="[isChanged(row, 'cdContract') ? 'changed' : '', isEmpty(row.cdContract) ? 'is-empty' : '']"
                    >
                      <option v-for="c in contractCodes(row)" :key="'cdContract' + index + c" :value="c">{{ c }}</option>
                    </select>
                    <div v-else class="font-bold text-indigo-900">{{ row.cdContract }}</div>
                  </div>

                  <!-- 営業所（東京／千葉。必須）。最低賃金はこの営業所のものと比べる。
                       初期値は物件の住所の県（代勤専門は清掃員の住所の県）から入れるが、1人が東京と千葉の両方の物件を持つこともあるので行ごとに選べる -->
                  <div class="mt-3">
                    <div class="cell-cap" v-tooltip="salesOfficeTip">{{ salesOfficeCaption }}</div>
                    <select
                      v-if="isEditable(row)"
                      v-model="row.salesOffice"
                      v-tooltip="salesOfficeTip"
                      class="cell-select text-center"
                      :class="[isChanged(row, 'salesOffice') ? 'changed' : '', isEmpty(row.salesOffice) ? 'is-empty' : '']"
                    >
                      <option value=""></option>
                      <option v-for="o in salesOffices" :key="'salesOffice' + index + o" :value="o">{{ o }}</option>
                    </select>
                    <div v-else class="office-view font-bold text-indigo-900" :class="[isChanged(row, 'salesOffice') ? 'changed' : '', isEmpty(row.salesOffice) ? 'is-empty' : '']">{{ row.salesOffice || '-' }}</div>
                  </div>

                  <!-- 担当営業所（スマイルのエリア名）。候補は AREA_SELECTIONS。契約書に出るので必須（空なら薄い赤・申請時に止める）
                       候補に無い古い名前（東京西エリアなど）を持つ行は、その値も候補に足して見える・選べるようにする。自由入力はさせない
                       雇用指示営業所は画面に出さない（データは持つ。areaFields を見る） -->
                  <div v-for="a in areaFields" :key="a.key + index" class="mt-3">
                    <div class="cell-cap">{{ a.caption }}</div>
                    <multiselect
                      v-if="isEditable(row)"
                      v-model="row[a.key]"
                      :options="areaOptions(row[a.key])"
                      :multiple="false"
                      :show-labels="false"
                      :searchable="true"
                      :allow-empty="true"
                      :max-height="200"
                      class="multiselect area-select"
                      :class="[isChanged(row, a.key) ? 'changed' : '', isEmpty(row[a.key]) ? 'is-empty' : '']"
                      placeholder="選ぶ"
                      @input="reload++"
                    ></multiselect>
                    <div v-else class="office-view font-bold text-indigo-900" :class="[isChanged(row, a.key) ? 'changed' : '', isEmpty(row[a.key]) ? 'is-empty' : '']">{{ row[a.key] || '-' }}</div>
                  </div>

                  <!-- 得意先への連絡。見落とすと連絡漏れになるので、枠と色を付けて目立たせる。
                       どこへ連絡するのか分かるよう、得意先名と支店名も併せて出す -->
                  <!-- 状態クラスに on / off は使わない（共通CSSが !important で色を付けるため） -->
                  <!-- 枠全体を label にして、得意先名の上を押してもチェックが切り替わるようにする（チェックボックスが小さく押しにくいため） -->
                  <label v-if="showContact(row)" class="contact" :class="canContact(row) ? '' : 'blocked'" v-tooltip="canContact(row) ? '' : '修正（上書き）で得意先へ連絡するのは作業終了のときです。終了日を入れてください。'">
                    <span class="contact-check">
                      <input type="checkbox" v-model="row.hasContactClient" :disabled="!canContact(row)" />
                      得意先へ要連絡
                    </span>
                    <div v-if="row.contactClient1" class="contact-client">{{ row.contactClient1 }}</div>
                    <div v-if="row.contactClient2" class="contact-client">{{ row.contactClient2 }}</div>
                  </label>

                  <div class="prop-total">週 {{ weekHours(row) }} h</div>

                  <!-- 週（月〜日の7曜日分の勤務）のコピー・貼付（契約詳細入力画面・受注タスクと同じ。ユーザー 2026-10-09）
                       コピーは入力できない行からもできる（今の勤務を元に、新しい行へ写すため）。貼付は入力できる行だけ。曜日のコピーとは別に持つ -->
                  <div v-if="anyEditable && !row.isCancelled" class="week-copy">
                    <button type="button" class="day-btn copy" @click="copyWeek(row)">週をコピー</button>
                    <button v-if="isEditable(row)" type="button" class="day-btn paste" :disabled="!copiedWeek" @click="pasteWeek(row)">週に貼付</button>
                  </div>
                </td>

                <!-- 適用期間 -->
                <td>
                  <div class="apply">
                    <div class="date-cap">開始日</div>
                    <!-- 編集できないときは入力欄ではなく文字（契約詳細入力画面の契約期間と同じ YYYY/MM/DD） -->
                    <WmsDateField
                      v-if="isEditable(row)"
                      :name="'startDateWork' + index"
                      :data-focus="'startDateWork' + index"
                      caption=""
                      :disabled="readOnly"
                      v-model="row.startDateWork"
                      :min="minStartDateWork(row)"
                      :clearable="true"
                      :class="[isChanged(row, 'startDateWork') ? 'changed' : '', isEmpty(row.startDateWork) ? 'is-empty' : '', isNg(index, 'startDateWork') ? 'ng' : '']"
                      @change="setSiteInfo(row.cdSite)"
                    />
                    <div v-else class="date-view" :class="[isChanged(row, 'startDateWork') ? 'changed' : '', isEmpty(row.startDateWork) ? 'none' : '', isNg(index, 'startDateWork') ? 'ng' : '']">
                      {{ formatDate(row.startDateWork) || '---' }}
                    </div>

                    <!-- 未保存の行にいきなり作業終了日が入ることはないため、入力させない -->
                    <div class="date-cap">終了日</div>
                    <!-- シーケンス無しの場合、終了日が無いことを示す -->
                    <div v-if="!row.sequenceNumber" class="no-date" :class="isNg(index, 'endDateWork') ? 'ng' : ''">---</div>
                    <!-- 変更前の行の終了日は、変更後の開始日が決まると自動で入る -->
                    <div v-else-if="row.isChangedFrom && !row.endDateWork" class="auto-end" :class="isNg(index, 'endDateWork') ? 'ng' : ''">
                      変更後の開始日を<br>入力すると自動で変更
                    </div>
                    <WmsDateField
                      v-else-if="canInputEndDate(row)"
                      :name="'endDateWork' + index"
                      caption=""
                      :disabled="readOnly"
                      v-model="row.endDateWork"
                      :min="row.startDateWork"
                      :clearable="true"
                      :class="[isChanged(row, 'endDateWork') ? 'changed' : '', row.mode === 'ended' && isEmpty(row.endDateWork) ? 'is-empty' : '', isNg(index, 'endDateWork') ? 'ng' : '']"
                    />
                    <!-- 編集できないときは文字。空欄だと未入力なのか分からないため、終了日が無いことは --- で示す -->
                    <div v-else class="date-view" :class="[isChanged(row, 'endDateWork') ? 'changed' : '', isEmpty(row.endDateWork) ? 'none' : '', isNg(index, 'endDateWork') ? 'ng' : '']">
                      {{ formatDate(row.endDateWork) || '---' }}
                    </div>
                  </div>
                </td>

                <!-- 曜日別 -->
                <td
                  v-for="w in weekList"
                  :key="'day' + index + w.id"
                  class="day-cell align-top"
                  :class="row['attendance' + w.id]&&row['attendance' + w.id].length?'bg-green-50':''"
                >
                  <div class="day">
                    <!-- 勤務形態（未選択なら休み扱い） -->
                    <multiselect
                      v-if="isEditable(row)"
                      v-model="row['attendance' + w.id]"
                      :options="attendanceList"
                      :multiple="true"
                      :close-on-select="false"
                      :show-labels="false"
                      :searchable="false"
                      :allow-empty="true"
                      :hide-selected="true"
                      :max-height="160"
                      label="name"
                      track-by="id"
                      class="multiselect"
                      :class="[isChanged(row, 'attendance' + w.id) ? 'changed' : '', isNg(index, 'attendance' + w.id) ? 'ng' : '']"
                      placeholder="-"
                      @input="reload++"
                    ></multiselect>

                    <!-- 編集不可の場合 -->
                    <div v-else-if="!isOff(row, w)" :class="isNg(index, 'attendance' + w.id) ? 'ng' : ''">
                      <div
                        v-for="(a, k) in row['attendance' + w.id]"
                        :key="'at' + index + w.id + k"
                        class="attendance-label"
                        :class="isChanged(row, 'attendance' + w.id) ? 'changed-text' : ''"
                      >
                        {{ a.name }}
                      </div>
                    </div>

                    <template v-if="!isOff(row, w)">
                      <!-- 開始〜終了 -->
                      <div class="times">
                        <AkbsTimeInput
                          v-model="row['start' + w.id]"
                          :disabled="!isEditable(row)"
                          :class="[isChanged(row, 'start' + w.id) ? 'changed' : '', isEmpty(row['start' + w.id]) ? 'is-empty' : '', isNg(index, 'start' + w.id) ? 'ng' : '']"
                          @change="setTime(row, w.id)"
                        />
                        <span class="tilde">~</span>
                        <AkbsTimeInput
                          v-model="row['end' + w.id]"
                          :disabled="!isEditable(row)"
                          :class="[isChanged(row, 'end' + w.id) ? 'changed' : '', isEmpty(row['end' + w.id]) ? 'is-empty' : '', isNg(index, 'end' + w.id) ? 'ng' : '']"
                          @change="setTime(row, w.id)"
                        />
                      </div>

                      <!-- 実働 -->
                      <div class="work">
                        実働
                        <input
                          type="number"
                          v-model="row['workingHours' + w.id]"
                          onwheel="return false;"
                          :disabled="!isEditable(row)"
                          :class="[isChanged(row, 'workingHours' + w.id) ? 'changed' : '', !Number(row['workingHours' + w.id]) ? 'is-empty' : '', isNg(index, 'workingHours' + w.id) ? 'ng' : '']"
                          @change="getBreakHours(row, w.id)"
                        />
                        h
                      </div>

                      <!-- 休憩（開始・終了・実働から算出） -->
                      <div class="rest">休憩 <b>{{ row['breakHours' + w.id] || 0 }}</b> h</div>
                    </template>

                    <!-- コピペ（休みの曜日にも貼り付けられるよう、勤務の有無に関わらず出す） -->
                    <div v-if="isEditable(row)" class="day-copy">
                      <button
                        type="button"
                        class="day-btn copy"
                        @click="copy(row, w.id)"
                      >
                        コピー
                      </button>
                      <button
                        type="button"
                        class="day-btn paste"
                        :disabled="!hasCopied"
                        @click="paste(row, w.id)"
                      >
                        貼付
                      </button>
                    </div>
                  </div>
                </td>

                <!-- 作業内容（曜日と勤務条件の間の列） -->
                <td class="center attr-start">
                  <select
                    v-model="row.cdContentsWork"
                    class="cell-select text-center"
                    :class="[isChanged(row, 'cdContentsWork') ? 'changed' : '', isEmpty(row.cdContentsWork) ? 'is-empty' : '']"
                    :disabled="!isEditable(row)"
                  >
                    <option v-for="s in workSelections" :key="s.id" :value="s.id">{{ s.name }}</option>
                  </select>
                </td>

                <!-- 祝日勤務（FULL・無は選択、それ以外は時間を直接入力する） -->
                <td class="center attr">
                  <select
                    :value="holidayKind(row)"
                    class="cell-select text-center"
                    :class="[isChanged(row, 'cdKubunHoliday') ? 'changed' : '', isEmpty(row.cdKubunHoliday) ? 'is-empty' : '', isNg(index, 'cdKubunHoliday') ? 'ng' : '']"
                    :disabled="!isEditable(row)"
                    @change="changeHolidayKind(row, $event.target.value)"
                  >
                    <option value="100">FULL</option>
                    <option value="hours">時間入力</option>
                    <option value="0">無</option>
                  </select>

                  <!-- 時間は 0.25 刻みの小数も入れられるよう、普通の number 入力にする（NumericTextInput2 は整数に丸めてしまう） -->
                  <div v-if="holidayKind(row) === 'hours'" class="holiday-hours">
                    <input
                      type="number"
                      v-model="row.cdKubunHoliday"
                      step="0.25"
                      min="0"
                      :disabled="!isEditable(row)"
                      class="hours-input"
                      :class="[isChanged(row, 'cdKubunHoliday') ? 'changed' : '', !Number(row.cdKubunHoliday) ? 'is-empty' : '', isNg(index, 'cdKubunHoliday') ? 'ng' : '']"
                      @wheel="$event.target.blur()"
                      @change="reload++"
                    />
                    <span class="unit">時間</span>
                  </div>
                </td>

                <!-- ゴミ搬出 -->
                <td class="center attr">
                  <!-- 有り｜無しは2つ並びのボタン（交通費区分と同じ作り）。未入力はどちらも塗らず薄い赤、ロック中は文字だけ -->
                  <div class="flag-kind" :class="[isChanged(row, 'hasCarryingoutGarbage') ? 'changed-kind' : '', isEmpty(row.hasCarryingoutGarbage) ? 'is-empty' : '', isNg(index, 'hasCarryingoutGarbage') ? 'ng' : '']">
                    <template v-if="isEditable(row)">
                      <button
                        v-for="s in flagSelections"
                        :key="'garbage' + index + s.id"
                        type="button"
                        class="kind-btn"
                        :class="!isEmpty(row.hasCarryingoutGarbage) && Number(row.hasCarryingoutGarbage) === s.id ? 'selected' : ''"
                        @click="row.hasCarryingoutGarbage = s.id"
                      >
                        {{ s.name }}
                      </button>
                    </template>
                    <span v-else class="kind-text">{{ flagText(row.hasCarryingoutGarbage) }}</span>
                  </div>
                </td>

                <!-- 全社祝日出勤 -->
                <td class="center attr">
                  <div class="flag-kind" :class="[isChanged(row, 'hasExportingCompanysHoliday') ? 'changed-kind' : '', isEmpty(row.hasExportingCompanysHoliday) ? 'is-empty' : '', isNg(index, 'hasExportingCompanysHoliday') ? 'ng' : '']">
                    <template v-if="isEditable(row)">
                      <button
                        v-for="s in flagSelections"
                        :key="'companyHoliday' + index + s.id"
                        type="button"
                        class="kind-btn"
                        :class="!isEmpty(row.hasExportingCompanysHoliday) && Number(row.hasExportingCompanysHoliday) === s.id ? 'selected' : ''"
                        @click="row.hasExportingCompanysHoliday = s.id"
                      >
                        {{ s.name }}
                      </button>
                    </template>
                    <span v-else class="kind-text">{{ flagText(row.hasExportingCompanysHoliday) }}</span>
                  </div>
                </td>
              </tr>

              <!-- 賃金行（勤務の終了日より後に始まる賃金は出さない。承認時にバックが消すもので、見せると迷うため） -->
              <template v-for="(wage, wi) in row.wages">
              <tr
                v-if="!isWageAfterEnd(row, wage)"
                :key="'wage' + index + '-' + wi"
                class="wage"
                :class="[rowClass(row), wi === 0 ? 'wage-first' : '', wage.isChangedFrom ? 'wage-before' : '']"
              >

                <!-- 適用日。上に「└ 賃金」の枝線を付けて、勤務行にぶら下がっていることを示す。
                     賃金の行は適用日だけで終了日は持たない（次の賃金の適用日の前日まで効く）。
                     縦幅を取らないよう、見出しと値を横に並べる（.wage-apply） -->
                <td>
                  <div class="wage-branch">
                    <span class="branch"></span>賃金
                    <!-- 賃金のみ変更で2件並ぶため、どちらの賃金か分かるようにする
                         「変更前」は元からある賃金だけ。足した賃金は、その後ろにさらに足して変更元になっていても「変更後」のまま -->
                    <span v-if="wage.isChangedTo" class="wage-state after">（変更後）</span>
                    <span v-else-if="wage.isChangedFrom" class="wage-state before">（変更前）</span>
                  </div>
                  <div class="apply wage-apply">
                    <!-- 賃金の開始日を手で入れるのは、賃金のみ変更で足した賃金だけ。
                         新規追加・契約内容変更は勤務の開始日と連動して自動で入り、修正（上書き）は動かすと賃金の無い日ができるため変更不可 -->
                    <div class="date-cap">適用日</div>
                    <!-- 編集できないときは入力欄ではなく文字（YYYY/MM/DD） -->
                    <WmsDateField
                      v-if="wage.isChangedTo"
                      :name="'wageStartDate' + index + '-' + wi"
                      :data-focus="'wageStartDate' + index + '-' + wi"
                      caption=""
                      :disabled="readOnly"
                      v-model="wage.startDate"
                      :min="minWageStartDate(row, wage, wi)"
                      :max="row.endDateWork"
                      :clearable="true"
                      :class="[isChangedWage(row, wi, 'startDate') ? 'changed' : '', isEmpty(wage.startDate) ? 'is-empty' : '', isNg(index, 'wage' + wi + '.startDate') ? 'ng' : '']"
                    />
                    <div v-else class="date-view" :class="[isChangedWage(row, wi, 'startDate') ? 'changed' : '', isEmpty(wage.startDate) ? 'none' : '', isNg(index, 'wage' + wi + '.startDate') ? 'ng' : '']">
                      {{ formatDate(wage.startDate) || '---' }}
                    </div>
                  </div>
                </td>

                <!-- 時給・交通費（曜日＋作業内容＋勤務条件3列）。
                     横一列（入力が左から右で終わり、縦に伸びない）。列を揃えた小さな表も試したが、ユーザーはこちらを選んだ（2026-09-18） -->
                <td :colspan="weekList.length + 4" class="relative">
                  <!-- 賃金のみ変更の境目。行は増やさず、時給の下の罫線に矢印を重ねる -->
                  <span v-if="wage.isChangedFrom" class="wage-arrow">
                    <Icon iconName="ArrowDown" iconType="outline" :strokeWidth="2" class="w-6 h-7" />
                  </span>

                  <div class="wage-fields">
                    <div class="wf">
                      <b>時給</b>
                      <NumericTextInput2
                        :name="'basicWage' + index + '-' + wi"
                        caption=""
                        v-model="wage.basicWage"
                        :disabled="!isWageEditable(row, wage)"
                        class="num-input"
                        :class="[isChangedWage(row, wi, 'basicWage') ? 'changed' : '', !Number(wage.basicWage) ? 'is-empty' : '', isNg(index, 'wage' + wi + '.basicWage') ? 'ng' : '']"
                      />
                      <span class="unit">円/h</span>
                    </div>

                    <!-- 交通費は1かたまりにして、時給と分ける -->
                    <div class="fare-box">
                      <div v-for="fare in fareItems" :key="'fare' + index + '-' + wi + fare.key" class="wf">
                        <b>{{ fare.caption }}</b>

                        <div class="fare-body">
                          <!-- 支給区分。選択肢が少ないのでボタンで並べる -->
                          <div class="fare-kind" :class="[isChangedWage(row, wi, fare.typeKey) ? 'changed-kind' : '', isNg(index, 'wage' + wi + '.' + fare.typeKey) ? 'ng' : '']">
                            <template v-if="isWageEditable(row, wage)">
                              <button
                                v-for="s in provisionSelections"
                                :key="'kind' + index + '-' + wi + fare.key + s.id"
                                type="button"
                                class="kind-btn"
                                :class="fareType(wage, fare) === s.id ? 'selected' : ''"
                                @click="changeFareType(wage, fare, s.id)"
                              >
                                {{ s.name }}
                              </button>
                            </template>

                            <!-- ロック中はボタンに見せず、選ばれている区分だけ出す -->
                            <span v-else class="kind-text">{{ selectionName(provisionSelections, fareType(wage, fare)) }}</span>
                          </div>

                          <div class="fare-fee">
                            <NumericTextInput2
                              :name="fare.feeKey + index + '-' + wi"
                              caption=""
                              v-model="wage[fare.feeKey]"
                              :disabled="!isWageEditable(row, wage)"
                              class="num-input"
                              :class="[isChangedWage(row, wi, fare.feeKey) ? 'changed' : '', isFareEmpty(wage, fare) ? 'is-empty' : '', isNg(index, 'wage' + wi + '.' + fare.feeKey) ? 'ng' : '']"
                            />
                            <span class="unit">円</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 一番下の賃金の右（駐輪の隣）に、賃金を足すボタンと、足した賃金を取り除くボタン
                         最低賃金が途中で上がるときに、新しい適用日からの賃金を続けられるように -->
                    <div v-if="wage === effectiveLastWage(row) && canAddWage(row)" class="wage-ops">
                      <button
                        type="button"
                        class="op-btn keep add-wage"
                        v-tooltip="'新しい適用日からの賃金を足します（前の賃金はその前日まで）'"
                        @click="addWage(row)"
                      >
                        <Icon iconName="Plus" iconType="outline" :strokeWidth="2" class="w-3.5 h-3.5 mr-1" />
                        賃金を追加
                      </button>
                      <button
                        v-if="wage.isChangedTo"
                        type="button"
                        class="op-btn cancel add-wage"
                        v-tooltip="'この賃金を取り除きます'"
                        @click="removeWage(row, wi)"
                      >
                        <Icon iconName="X" iconType="outline" :strokeWidth="2" class="w-3.5 h-3.5 mr-1" />
                        この賃金を削除
                      </button>
                    </div>

                    <!-- 保存時は「時給+交通費」を契約単価と比べるので、その値を先に見せる（交通費の枠の外）。
                         次の賃金がある（適用が終わっている）賃金は今の条件ではないため出さない -->
                    <div v-if="wage === effectiveLastWage(row)" class="fare-sum">
                      <div class="hourly" :class="overWage(row, wage) ? 'over' : ''">
                        時給+交通費 <b>{{ formatNumber(hourlyWage(row, wage)) }}</b> 円/h
                        <template v-if="contractWage(row)">
                          ／ 契約単価 {{ formatNumber(contractWage(row)) }} 円/h
                          <span v-if="overWage(row, wage)">超過 +{{ formatNumber(overWage(row, wage)) }}</span>
                        </template>
                      </div>

                      <div class="sum-row">
                        <!-- 作業終了した物件は月額を出さない -->
                        <div v-if="!row.endDateWork" class="fare-total">
                          賃金 {{ monthlyWage(row, wage) }} 円/月
                        </div>
                        <div class="fare-total">交通費 {{ monthlyFare(row, wage) }} 円/月</div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>

              </template>

              <!-- 備考。文章が入るので幅を取り、操作列と物件名は縦につながっているためその分は除く。
                   ロック中は中身があるときだけ出して、無駄に縦へ伸ばさない -->
              <tr v-if="showNote(row)" :key="'note' + index" class="wage" :class="rowClass(row)">
                <!-- 見出しは適用期間の列に置き、入力欄はその先を全部使う -->
                <td class="center note-cap">雇用指示 備考</td>
                <td :colspan="weekList.length + 4">
                  <!-- autocomplete="off"：ブラウザが過去に入れた文を候補に出さないようにする（2026-09-24） -->
                  <input
                    type="text"
                    :name="'note' + index"
                    v-model="row.note"
                    :disabled="!isEditable(row)"
                    autocomplete="off"
                    class="note-input"
                    :class="isChanged(row, 'note') ? 'changed' : ''"
                  />
                </td>
              </tr>

              </template>

              <tr :key="'l'+index" class="border-none">
                <td :colspan="weekList.length + 7" class="border-none">
                  <!-- 行の区切り。変更元の行なら、変更後の行がすぐ下に続くので線の真ん中に矢印を置く -->
                  <div class="row-line" :class="row.isChangedFrom ? 'changed' : ''">
                    <span v-if="row.isChangedFrom" class="change-arrow">
                      <Icon iconName="ArrowDown" iconType="solid" class="w-7 h-7" />
                    </span>
                  </div>
                </td>
              </tr>
            </template>

            <tr v-if="!value.length">
              <td :colspan="weekList.length + 7" class="text-center text-gray-400 py-4">
                雇用指示が登録されていません。「物件を追加」から登録できます。
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="px-3 py-3">
        <span class="text-xs text-gray-500">
          ※ 時間・休憩は実労働時間を入力してください。勤務がない日は勤務形態を空にしてください。
        </span>
      </div>

    </div>

    <!-- 物件選択モーダル -->
    <SelectSiteModal
      v-model="modalOpen"
      :modalData="modalData"
      :open="modalOpen"
      @selected="selectedSite"
      @closed="modalOpen=false"
    />
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import Icon from '@components/Icon.vue'
import PrimaryButton from '@components/PrimaryButton.vue'
import NumericTextInput2 from '@components/NumericTextInput2.vue'
import Toggle from '@components/Toggle.vue'
import WmsDateField from '@wmscomponents/WmsDateField.vue'
import AkbsTimeInput from './AkbsTimeInput.vue'
// import WmsSuggestInput from '@wmscomponents/WmsSuggestInput.vue'
import SelectSiteModal from './SelectSiteModal.vue'

import * as moment from 'moment'
import * as backend from '@libs/backend'
import * as utils from '@libs/utils'
import * as dialogs from '@libs/dialogs'
import * as utilsAKBS from '@libs/utilsAKBS'
import * as constantsAKBS from '@libs/constantsAKBS'

// 有り・無し（DBは integer の 1／0）
const FLAG_SELECTIONS = [
  { id: 1, name: '有り' },
  { id: 0, name: '無し' }
]

// 交通費（区分・金額のキー）。親の保存時チェックでも使うので export する
export const FARE_ITEMS = [
  { key: 'train', caption: '電車', typeKey: 'kubunTrain', feeKey: 'fareTrain' },
  { key: 'bus', caption: 'バス', typeKey: 'kubunBus', feeKey: 'fareBus' },
  { key: 'parking', caption: '駐輪', typeKey: 'kubunBicycleParking', feeKey: 'fareBicycleParking' },
]

// 取得時と突き合わせる項目（背景を黄色にしている入力欄と同じもの）
const ROW_KEYS = [
  'cdContract',
  'salesOffice',
  'nameSalesOfficeInstructEmployment',
  'nameSalesOfficeCharge',
  'cdContentsWork',
  'startDateWork',
  'endDateWork',
  'cdKubunHoliday',
  'hasCarryingoutGarbage',
  'hasExportingCompanysHoliday',
  'note',
]

// 曜日ごとに突き合わせる項目（後ろに曜日を付けてキーにする）
const DAY_KEYS = ['attendance', 'start', 'end', 'workingHours']

// 実際の連絡先と、その元になる得意先（物件から取り込んだもの）の対応
const CONTACT_KEYS = [
  { contact: 'contactClient1', from: 'client1' },
  { contact: 'contactClient2', from: 'client2' },
  { contact: 'contactFax', from: 'fax' },
]

// 賃金で突き合わせる項目
const WAGE_KEYS = ['startDate', 'basicWage']
for (const fare of FARE_ITEMS) {
  WAGE_KEYS.push(fare.typeKey, fare.feeKey)
}
// 交通費の金額のキー（比較のとき空と 0 を同じに見る）
const FEE_KEYS = FARE_ITEMS.map(f => f.feeKey)

/**
 * 雇用指示1件分の空データを作る
 * 曜日別のキー名は InterviewsDCComponents と揃えている（utilsAKBS の計算をそのまま使うため）
 */
export function createEmployment() {
  const row = {
    // 保存済みなら採番される。未保存（null）の行は最初から入力できる
    sequenceNumber: null,

    // 契約内容変更で足した行だけ、変更元の sequenceNumber が入る（承認後は本番DBにも残る）
    changeFromSequenceNumber: null,

    // 今回の申請で契約内容変更をしたかどうか（この申請の画面だけで使う）
    // changeFromSequenceNumber は過去の変更でも入っているため、表示の判定には使えない
    isChangedFrom: false,
    isChangedTo: false,

    // この申請で足した行か（取消で行ごと消すかどうかの判定に使う）
    // 先頭が _ のキーはバックの convertToCamelCase で名前が変わって読み戻せないため、_isNew のような名前にしない
    isNew: false,

    // この雇用指示を取り消すか（保存済みの行だけ。物件を追加したが1日も働かずに辞めた等）
    // AKBS 登録時にバックが mst_instruct_employment から mst_instruct_employment_cancelled へ移す（最初から無かった扱い）
    // 押せるのは開始日から 21 日後の当日まで。一度付いていれば承認後は過ぎていてもそのまま通す
    isCancelled: false,

    // 物件
    site: '',
    siteAddress: '',
    cdContract: '',
    cdContentsWork: '',

    // 営業所（東京／千葉。必須。最低賃金はこの営業所のものと比べる）
    // 初期値は物件の住所の県から。代勤専門（000000000）は清掃員の住所の県から。決まらなければ空で選ばせる
    salesOffice: '',

    // 雇用指示営業所・担当営業所（スマイルのエリア名。候補は AREA_SELECTIONS、候補に無い古い名前も今の値は選べる。担当営業所は契約書に出るので必須）
    // 初期値は物件の住所（代勤専門は清掃員の住所）から決まるエリアを2つとも同じ値で入れる
    nameSalesOfficeInstructEmployment: '',
    nameSalesOfficeCharge: '',

    // 適用期間
    startDateWork: '',
    endDateWork: '',

    // 備考
    note: '',

    // 得意先（物件から取り込んだもの。書き換えない）
    cdClient: '',
    client1: '',
    client2: '',
    fax: '',

    // 実際の連絡先（初期値は上と同じ。得意先のデータが古いときに送信者が書き換える）
    contactClient1: '',
    contactClient2: '',
    contactFax: '',

    // 得意先への連絡が必要か（新規追加・作業終了は最初から必要な扱いにする）
    hasContactClient: false,

    // 勤務条件（祝日勤務は「無」も選択肢のため、未入力は空文字で持つ）
    cdKubunHoliday: '',
    hasCarryingoutGarbage: '',
    hasExportingCompanysHoliday: '',

    // 賃金情報（保存済みなら id が付く。バックは id があれば更新、無ければ新規登録する）
    // 賃金の行は適用日（startDate）だけで終了日は持たない（次の賃金の適用日の前日まで効く）
    // isChangedFrom / isChangedTo は今回の申請で賃金のみ変更をしたかどうか
    wages: [{
      id: null,
      startDate: '',
      basicWage: '',
      isChangedFrom: false,
      isChangedTo: false,
    }],

    // 画面上の状態（保存時は除外する）
    mode: 'locked',
  }

  for (const w of constantsAKBS.WEEK_LIST) {
    row['attendance' + w.id] = []
    row['start' + w.id] = ''
    row['end' + w.id] = ''
    row['workingHours' + w.id] = ''
    row['breakHours' + w.id] = ''
  }

  // 区分は「無」始まりなので、金額は0で揃える
  for (const fare of FARE_ITEMS) {
    row.wages[0][fare.typeKey] = 0
    row.wages[0][fare.feeKey] = '0'
  }

  return row
}

export default {
  components: {
    Multiselect,
    Icon,
    PrimaryButton,
    NumericTextInput2,
    Toggle,
    WmsDateField,
    AkbsTimeInput,
    // WmsSuggestInput
    SelectSiteModal,
  },

  props: {
    // 雇用指示の配列
    value: {
      type: Array,
      default: () => []
    },

    // 受注した契約の情報。物件コードをキーにした { 物件コード: { siteWage, siteShort, contract } }
    // 親の入力画面の情報（val）に持たせる。AKBS登録前は開くたびに取り直し、登録時に req_akbs へ控えて、登録後はその控えを出す
    contractList: {
      type: Object,
      default: () => ({})
    },

    // 取得時のAKBS情報（編集を取り消すときに、ここから元の値へ戻す）
    original: {
      type: Object,
      default: () => ({})
    },

    // 清掃員名
    name: {
      type: String,
      default: ''
    },

    // 清掃員コード
    staffDailyCleaning: {
      type: String,
      default: ''
    },

    // 清掃員の住所の都道府県・市区町村（代勤専門の行の営業所・エリアの初期値を決めるのに使う）
    staffPref: {
      type: String,
      default: ''
    },
    staffCity: {
      type: String,
      default: ''
    },

    // 終わった雇用指示も出しているか（親が持つ。チェックを変えると update:showEnded を出し、親が行を取ってきて足す・取り除く）
    showEnded: {
      type: Boolean,
      default: false
    },

    // 「終了した雇用指示も表示」のチェックを出すか（入社・再入社では出さない。親が判定する）
    showEndedToggle: {
      type: Boolean,
      default: true
    },

    // 「終了した雇用指示も表示」のチェックを押せなくするか（承認待ち＝承認のとき。親が判定する）
    endedToggleDisabled: {
      type: Boolean,
      default: false
    },

    // 閲覧のみか（承認待ち・AKBS登録済み）。親の fieldset の disabled は日付欄の「×」（Icon）には効かないので、日付欄にはこれで disabled を渡す
    readOnly: {
      type: Boolean,
      default: false
    },

    // 契約詳細の申請のお知らせ（この清掃員が働いている契約の終了・変更。親が akbsContractForm/getStaffNotices で取る）
    // 見出しの帯に「契約終了の申請あり」を出して、作業終了のし忘れを防ぐ
    contractNotices: {
      type: Array,
      default: () => []
    },

    // 「物件を追加」で足した行の開始日の初期値（新規入社・再入社のときは入社日。親が渡す。空なら入れない。ユーザー指示）
    defaultStartDate: {
      type: String,
      default: ''
    },

    // 保存時の矛盾チェックでNGになった欄（'行の位置.キー' の配列。'0.startDateWork' や '2.row' など）
    // 親が持ち、どこがNGか赤い枠で示すために使う
    errors: {
      type: Array,
      default: () => []
    },
  },

  data() {
    return {
      reload: 0,

      // 開閉
      opened: true,

      // 畳まれた「変更なし」の雇用指示を手で開いたか（シーケンス → 開いたか）
      seqOpened: {},

      // 曜日リスト
      weekList: constantsAKBS.WEEK_LIST,

      // 勤務形態リスト
      attendanceList: utils.clone(constantsAKBS.ATTENDANCE),

      // 作業内容
      workSelections: constantsAKBS.WORK_SELECTIONS,

      // 営業所（東京／千葉）。画面では「最低賃金判定」と出す
      salesOffices: constantsAKBS.SALES_OFFICE_SELECTIONS,
      salesOfficeCaption: constantsAKBS.SALES_OFFICE_CAPTION,
      salesOfficeTip: constantsAKBS.SALES_OFFICE_TIP,

      // 担当営業所の候補（エリア名）と欄の定義
      // 雇用指示営業所（nameSalesOfficeInstructEmployment）は画面に出さない（お客様も何に使っているか分からない）。
      // データとしては持ち続ける：初期値は今までどおり住所から入れ、保存もする
      areaSelections: constantsAKBS.AREA_SELECTIONS,
      areaFields: [
        // 画面では「エリア」と呼ぶ（列名は担当営業所 name_sales_office_charge のまま）
        { key: 'nameSalesOfficeCharge', caption: 'エリア' },
      ],

      // 祝日区分
      holidayType: constantsAKBS.HOLIDAYTYPE_SELECTIONS,

      // 有り・無し
      flagSelections: FLAG_SELECTIONS,

      // 交通費区分
      provisionSelections: constantsAKBS.PROVISION_SELECTIONS,

      // 交通費の項目
      fareItems: FARE_ITEMS,

      // コピーした曜日の勤務
      copied: {},

      // コピーした週（7曜日分）の勤務（曜日のコピーとは別に持つ）
      copiedWeek: null,

      // 契約内容の行を表示するか（全物件まとめて切り替える）
      showContract: true,

      // モーダル
      modalOpen: false,
      modalData: {},
      // 物件選択モーダルを開いた行の位置
      modalIndex: null

    }
  },

  watch: {
    // 読み込んだデータも含め、交通費が「無」の行は金額を0に揃える
    value: {
      handler() {
        this.fillKeys()
        this.fillBreakHours()
        this.fillZeroFare()
        this.fillWageStartDate()
        this.fillChangedEndDate()
        this.clearContact()
      },
      deep: true,
      immediate: true
    }
  },

  computed: {
    /**
     * 今も続いている物件（作業終了にした行と、終了日が入っている行は除く）
     * 週合計・月額の元になる
     */
    activeRows() {
      this.reload
      return this.value.filter(row => row.mode !== 'ended' && !row.endDateWork)
    },

    /**
     * 全物件の1週間の実働時間（作業終了した物件は除く）
     */
    totalHours() {
      const list = this.activeRows
      return list.length ? utilsAKBS.getHoursForOneWeek(list) : 0
    },

    /**
     * 全物件の月額（作業終了した物件は除く）
     */
    totalSalary() {
      const total = this.activeRows.reduce((sum, row) => sum + utilsAKBS.getMonthlyWage(row), 0)
      return this.formatNumber(total)
    },

    /**
     * 取得時の雇用指示（採番で引けるようにする）
     * 変更の判定は入力欄ごとに走るため、毎回配列を探さずに済むよう1度だけ組み立てる
     */
    originalRows() {
      const map = {}
      for (const e of (this.original && this.original.employment) || []) {
        if (e.sequenceNumber) {
          map[e.sequenceNumber] = e
        }
      }
      return map
    },

    /**
     * 比較する元データがあるか（新規入社は無いので、変更の判定をしない）
     */
    canCompare() {
      return !!(this.original && this.original.employment)
    },

    /**
     * 取得時から変わった項目の数（見出しに出す）
     * 数える項目は、背景を黄色にしている入力欄と同じ
     */
    changedCount() {
      if (!this.canCompare) {
        return 0
      }

      let count = 0

      for (const row of this.value) {
        // 足した物件は変更ではなく追加として数える
        if (!this.originalRow(row)) {
          continue
        }

        count += ROW_KEYS.filter(key => this.isChanged(row, key)).length

        for (const w of this.weekList) {
          count += DAY_KEYS.filter(key => this.isChanged(row, key + w.id)).length
        }

        for (let wi = 0; wi < row.wages.length; wi++) {
          count += WAGE_KEYS.filter(key => this.isChangedWage(row, wi, key)).length
        }
      }

      return count
    },

    /**
     * 足した物件の数（見出しに出す）
     */
    addedCount() {
      return this.canCompare ? this.value.filter(row => !this.originalRow(row)).length : 0
    },

    /**
     * 取り消した雇用指示の数（保存済みの行で「この雇用指示を取消」を押したもの。見出しに出す）
     */
    cancelledCount() {
      return this.value.filter(row => row.isCancelled && !!row.sequenceNumber).length
    },

    /**
     * 取得時から変わったところがあるか（変更・追加・取消のどれか。承認者が開いたときに表を開くかの判定に使う）
     */
    hasChanged() {
      return this.changedCount > 0 || this.addedCount > 0 || this.cancelledCount > 0
    },

    /**
     * コピー済みか（貼付ボタンの活性判定）
     */
    hasCopied() {
      return !!this.copied && !!Object.keys(this.copied).length
    },

    /**
     * 入力できる行があるか（無ければ週のコピー・貼付のボタンを出さない。承認待ち・登録済みの閲覧など）
     */
    anyEditable() {
      return this.value.some(row => this.isEditable(row))
    }
  },

  methods: {
    /**
     * 表を開く・閉じる（親の「全て開く」「全て閉じる」から呼ぶ）
     * @param {Boolean} opened 開くなら true
     */
    setOpened(opened) {
      this.opened = opened
    },

    /**
     * 編集できる状態か
     * 未保存（sequenceNumber なし）の行は「修正」を押さなくても入力できる
     */
    isEditable(row) {
      return !row.sequenceNumber || row.mode === 'edit' || row.mode === 'change'
    },

    /**
     * 未操作の行か（保存済みで、操作のボタンをまだ押していない。見出しの帯に「変更なし」を出す）
     * 契約内容変更の変更前の行は、変更後の行と対で見るものなので未操作にしない
     * @param {Object} row 対象行
     */
    isUntouched(row) {
      return !!row.sequenceNumber && !row.isChangedFrom && !row.isCancelled && !this.isEditable(row) && row.mode !== 'changeWage' && row.mode !== 'ended'
    },

    /**
     * この雇用指示を取り消せるか（保存済みで未操作の行。開始日から 21 日後の当日まで。開始日前も含む）
     * 契約内容変更の変更前・変更後の行は取り消せない（変更の取消で戻す）
     * @param {Object} row 対象行
     */
    canCancelRow(row) {
      if (!row.sequenceNumber || row.isChangedFrom || row.isChangedTo || !row.startDateWork) {
        return false
      }
      return moment().format('YYYY-MM-DD') <= moment(row.startDateWork).add(21, 'days').format('YYYY-MM-DD')
    },

    /**
     * この雇用指示を取消にする（AKBS 登録時に本体から取消の表へ移る）
     * 取り消す前に、この雇用指示に紐づく休み登録・代勤・振替・賃金の件数を出して確認する（残るものを知ったうえで決めてもらう）
     * @param {Object} row 対象行
     */
    async cancelRow(row) {
      let counts = null
      try {
        // API: akbsInputForm/countLinked
        const res = await backend.searchData('akbsInputForm/countLinked', { sequenceNumber: row.sequenceNumber, staffDailyCleaning: this.staffDailyCleaning })
        counts = res && res.data ? res.data.data : null
      } catch (e) {
        counts = null
      }

      let linked = '（紐づく登録の件数を取得できませんでした）'
      if (counts) {
        linked = `この雇用指示に紐づく登録：休み登録 ${counts.holiday} 件・代勤 ${counts.operation} 件・振替 ${counts.exchange} 件・賃金 ${counts.wage} 件`
      }
      const res = await dialogs.showConfirmDialog(
        'この雇用指示を取り消しますか？',
        `${row.site || row.cdSite}（契約コード ${row.cdContract || '-'}）の雇用指示を、最初から無かったものにします。\n${linked}\n休み登録・代勤・振替・賃金は消えずに残ります（稼働管理では「本来表示されません」の行に出ます）。\nAKBS へ登録するまでは「取消をやめる」で戻せます。`,
        '取り消す'
      )
      if (res !== 'YES') {
        return
      }
      this.setCancelled(row, true)
    },

    /**
     * 取消をやめる（AKBS 登録前だけ）
     * @param {Object} row 対象行
     */
    uncancelRow(row) {
      this.setCancelled(row, false)
    },

    /**
     * 取消の印を付ける・外す（親の validate からも呼ぶ。退職日より後に始まる行を自動で取り消すとき）
     * 取り消す行は入力を全部ロックし、得意先への連絡も外す
     * @param {Object} row 対象行
     * @param {Boolean} cancelled 取り消すなら true
     */
    setCancelled(row, cancelled) {
      row.isCancelled = !!cancelled
      row.mode = cancelled ? 'cancelled' : 'locked'
      if (cancelled) {
        row.hasContactClient = false
      }
      this.reload++
    },

    /**
     * 畳める行か（未操作で、ほかの雇用指示に変更・追加がある）
     * どの行もまだ触っていないうちは、どこを押せばよいか分かるように全部開いたままにする
     * @param {Object} row 対象行
     */
    canFold(row) {
      // 契約終了の申請があって作業終了がまだの行は、作業終了ボタンが見えるように畳まない
      const notice = this.rowNotice(row)
      if (notice && notice.todo) {
        return false
      }
      // 必須の最低賃金判定（営業所）・エリアが空の行は畳まない。畳むと欄が描かれず、申請時の未入力の数え上げ（.is-empty）から漏れて申請が通ってしまう
      if (this.isEmpty(row.salesOffice) || this.areaFields.some(a => this.isEmpty(row[a.key]))) {
        return false
      }
      return this.isUntouched(row) && this.value.some(e => !this.isUntouched(e))
    },

    /**
     * 畳んでいるか（畳める行は、手で開くまで見出しの帯の1行だけにする）
     * 保存時の矛盾チェックでNGになった行は、赤い枠が見えるように開く
     * @param {Object} row 対象行
     * @param {Number} index 行の位置
     */
    isFolded(row, index) {
      if (!this.canFold(row) || this.seqOpened[row.sequenceNumber]) {
        return false
      }
      return !this.errors.some(e => e.startsWith(index + '.'))
    },

    /**
     * 未入力の欄の一覧（表で薄い赤（.is-empty）にしている欄と同じ条件。取り消した行と畳んだ行は表に描いていないので数えない）
     * 親（AkbsInputForm）の申請・登録時の必須チェックが「どの雇用指示のどの欄か」を知らせるのに使う
     * 変更前（履歴として残る行・賃金のみ変更の変更前の賃金）は AKBS の古い値がそのまま残るだけで、この申請では直せないので数えない
     * 変更なし（ロック中）の行の欄は入る：AKBS の今の値が空のときで、直すには「修正（上書き）」が要る
     * @returns {Array<String>} 例：「○○マンション（契約コード 01）：祝日勤務、月の実働」
     */
    emptyFieldErrors() {
      const list = []
      this.value.forEach((row, index) => {
        if (row.isCancelled || row.isChangedFrom || this.isFolded(row, index)) {
          return
        }
        const fields = []
        if (!row.sequenceNumber && this.isEmpty(row.cdContract)) {
          fields.push('契約コード')
        }
        if (this.isEmpty(row.salesOffice)) {
          fields.push(this.salesOfficeCaption)
        }
        // エリア（担当営業所）は契約書に出るので必須
        for (const a of this.areaFields) {
          if (this.isEmpty(row[a.key])) {
            fields.push(a.caption)
          }
        }
        if (this.isEditable(row) && this.isEmpty(row.startDateWork)) {
          fields.push('開始日')
        }
        if (row.mode === 'ended' && this.isEmpty(row.endDateWork)) {
          fields.push('終了日')
        }
        for (const w of this.weekList) {
          if (this.isOff(row, w)) {
            continue
          }
          const day = []
          if (this.isEmpty(row['start' + w.id])) day.push('開始時間')
          if (this.isEmpty(row['end' + w.id])) day.push('終了時間')
          if (!Number(row['workingHours' + w.id])) day.push('実働')
          if (day.length) {
            fields.push(`${w.name}の${day.join('・')}`)
          }
        }
        if (this.isEmpty(row.cdContentsWork)) {
          fields.push('作業内容')
        }
        // 区分が空のときと、「時間入力」で時間が空（0）のとき
        if (this.isEmpty(row.cdKubunHoliday) || (this.holidayKind(row) === 'hours' && !Number(row.cdKubunHoliday))) {
          fields.push('祝日勤務')
        }
        if (this.isEmpty(row.hasCarryingoutGarbage)) {
          fields.push('ゴミ搬出')
        }
        if (this.isEmpty(row.hasExportingCompanysHoliday)) {
          fields.push('全社祝日出勤')
        }
        const wages = row.wages || []
        wages.forEach((wage, wi) => {
          if (this.isWageAfterEnd(row, wage) || wage.isChangedFrom) {
            return
          }
          const items = []
          if (wage.isChangedTo && this.isEmpty(wage.startDate)) items.push('適用日')
          if (!Number(wage.basicWage)) items.push('時給')
          for (const fare of this.fareItems) {
            if (this.isFareEmpty(wage, fare)) items.push(`${fare.caption}の金額`)
          }
          if (items.length) {
            fields.push(`${wages.length > 1 ? `賃金${wi + 1}` : '賃金'}の${items.join('・')}`)
          }
        })
        if (fields.length) {
          const site = row.site || '物件未選択'
          const con = row.cdContract ? `契約コード ${row.cdContract}` : '契約コード未選択'
          // 入力できない行（変更なし・賃金のみ変更）の勤務の欄は、この画面では「修正（上書き）」にしないと直せないので添える
          const hint = !this.isEditable(row) && fields.some(f => !f.startsWith('賃金')) ? '（この行は「修正（上書き）」にすると直せます）' : ''
          list.push(`${site}（${con}）：${fields.join('、')}${hint}`)
        }
      })
      return list
    },

    /**
     * この雇用指示の契約に出ている、契約詳細の申請のお知らせ（無ければ null。契約終了を優先）
     * todo：契約終了の申請があるのに、この雇用指示の終了日が空、または契約終了日より後
     * 契約内容変更の変更前の行は履歴なので出さない
     * @param {Object} row 対象行
     */
    rowNotice(row) {
      if (!row.cdSite || row.isChangedFrom) {
        return null
      }
      const list = this.contractNotices.filter(n => n.siteCode === String(row.cdSite) && n.cdContract === String(row.cdContract))
      const notice = list.find(n => n.mode === 'end') || list[0]
      if (!notice) {
        return null
      }
      const day = d => String(d || '').slice(0, 10).replace(/\//g, '-')
      const todo = notice.mode === 'end' && (!row.endDateWork || (!!notice.endDate && day(row.endDateWork) > day(notice.endDate)))
      return { ...notice, todo }
    },

    /**
     * 見出しの帯を押した（畳める行だけ開閉する）
     * キーを後から足すのでオブジェクトごと入れ替える
     * @param {Object} row 対象行
     */
    toggleFold(row) {
      if (!this.canFold(row)) {
        return
      }
      this.seqOpened = { ...this.seqOpened, [row.sequenceNumber]: !this.seqOpened[row.sequenceNumber] }
    },

    /**
     * 得意先への連絡を確認してもらう行か
     * 新規追加・作業終了のときと、修正（上書き）で作業終了日を入れたときだけ出す
     * （修正（上書き）で終了日が無いうちはチェックを付けられないので、枠ごと出さない）
     * @param {Object} row 対象行
     */
    showContact(row) {
      if (row.isCancelled) {
        return false
      }
      // 修正（上書き）は「何でもできる」操作（終了日を消して勤務を戻す、など）なので、終了日が無くても連絡の枠を出す（ユーザー決定）
      return (!row.sequenceNumber && !row.isChangedTo) || row.mode === 'ended' || row.mode === 'edit'
    },

    /**
     * 得意先への連絡にチェックを付けられるか
     * 修正（上書き）も含めて、枠を出している行は全部付けられる（前は上書きでは終了日が入るまで付けさせなかった）
     * @param {Object} row 対象行
     */
    canContact(row) {
      return !row.isCancelled
    },

    /**
     * 賃金を編集できる状態か
     * 賃金のみ変更のときは、足した賃金だけ入力させる（勤務内容は変えさせない）
     * @param {Object} row 対象行
     * @param {Object} wage 賃金情報
     */
    isWageEditable(row, wage) {
      return this.isEditable(row) || (row.mode === 'changeWage' && !!wage.isChangedTo)
    },

    /**
     * 勤務の開始日として選べる一番早い日（カレンダーの下限）
     * 契約内容変更の変更後の行は、変更前の開始日の翌日より前にはできない
     * （変更前の終了日が「変更後の開始日の前日」で自動で入るため、同じ日以前だと期間が壊れる）
     * @param {Object} row 対象行
     */
    minStartDateWork(row) {
      if (!row.isChangedTo || !row.changeFromSequenceNumber) {
        return ''
      }
      const from = this.value.find(e => e.isChangedFrom && e.sequenceNumber == row.changeFromSequenceNumber)
      return from && from.startDateWork ? utils.addDays(from.startDateWork, 1) : ''
    },

    /**
     * 賃金の開始日として選べる一番早い日（カレンダーの下限）
     * 賃金のみ変更で足した賃金は、変更前の賃金の開始日の翌日より前にはできない
     * @param {Object} row 対象行
     * @param {Object} wage 賃金情報
     * @param {Number} wi 賃金の位置
     */
    minWageStartDate(row, wage, wi) {
      if (!wage.isChangedTo || wi === 0) {
        return ''
      }
      const from = row.wages[wi - 1]
      return from && from.startDate ? utils.addDays(from.startDate, 1) : ''
    },

    /**
     * 作業終了日を入力できるか
     * 修正中のほか、作業終了にした行は終了日だけ入れてもらう
     */
    canInputEndDate(row) {
      return !!row.sequenceNumber && (this.isEditable(row) || row.mode === 'ended')
    },

    /**
     * 備考の行を出すか
     * ロック中は中身があるときだけ出して、無駄に縦へ伸ばさない
     * @param {Object} row 対象行
     */
    showNote(row) {
      return this.isEditable(row) || !!row.note
    },

    /**
     * 操作列・物件名のセルが縦につながる行数（勤務行＋賃金の行＋備考の行）
     * @param {Object} row 対象行
     */
    rowspan(row) {
      // 勤務の終了日より後に始まる賃金は表に出さないので、結合する行数に数えない
      const shown = (row.wages || []).filter(w => !this.isWageAfterEnd(row, w)).length
      return shown + 1 + (this.showNote(row) ? 1 : 0)
    },

    /**
     * 行の状態を表すクラス
     */
    rowClass(row) {
      const classes = [row.sequenceNumber ? row.mode : 'new']
      // 契約内容変更の変更前の行は全体を薄いグレーにする（契約詳細入力画面と同じ）
      if (row.isChangedFrom) {
        classes.push('before')
      }
      // 取り消した行は無かった扱いなので、薄いグレー＋取消線
      if (row.isCancelled) {
        classes.push('cancelled')
      }
      // 終了日が入っている行はもう続いていない勤務なので、薄いグレーにして今の勤務と区別する
      if (row.endDateWork) {
        classes.push('finished')
      }
      return classes.join(' ')
    },

    /**
     * 保存時の矛盾チェックでNGになった欄か（赤い枠にする）
     * @param {Number} index 行の位置
     * @param {String} key 欄のキー（'row' は行全体）
     */
    isNg(index, key) {
      return this.errors.includes(index + '.' + key)
    },

    /**
     * 勤務のない曜日か（勤務形態が未選択なら休み）
     */
    isOff(row, week) {
      const attendance = row['attendance' + week.id]
      return !attendance || !attendance.length
    },

    /**
     * 交通費区分（選択肢と突き合わせるため数値に直す）
     * @param {Object} wage 賃金情報
     * @param {Object} fare 交通費の項目（FARE_ITEMS の1要素）
     */
    fareType(wage, fare) {
      const value = wage[fare.typeKey]
      return this.isEmpty(value) ? '' : Number(value)
    },

    /**
     * 交通費区分を選び直した
     * @param {Object} wage 賃金情報
     * @param {Object} fare 交通費の項目（FARE_ITEMS の1要素）
     * @param {String} value 選んだ値
     */
    changeFareType(wage, fare, value) {
      wage[fare.typeKey] = Number(value)
      this.changeFare(wage, fare)
    },

    /**
     * 交通費区分の変更
     * 「無」を選んだら金額を0にし、支給ありに戻したら入力し直せるよう空にする
     * @param {Object} wage 賃金情報
     * @param {Object} fare 交通費の項目（FARE_ITEMS の1要素）
     */
    changeFare(wage, fare) {
      if (!wage[fare.typeKey]) {
        wage[fare.feeKey] = '0'
      } else if (wage[fare.feeKey] === '0') {
        wage[fare.feeKey] = ''
      }
    },

    /**
     * 取得したデータに無い項目を、空の雇用指示の値で作っておく
     * Vue は後から足した項目の変化を追えないため、これをやらないと
     * mode や note を代入しても画面が更新されないことがある
     * 既にある値は書き換えない
     */
    fillKeys() {
      const base = createEmployment()
      const baseWage = base.wages[0]

      for (const row of this.value) {
        // 実際の連絡先は、物件から取り込んだ得意先を初期値にする。
        // 項目を作るときだけ入れるので、送信者が空にしたものを勝手に戻すことはない
        for (const key of CONTACT_KEYS) {
          if (row[key.contact] === undefined) {
            this.$set(row, key.contact, row[key.from] || '')
          }
        }

        for (const key of Object.keys(base)) {
          if (row[key] === undefined) {
            this.$set(row, key, utils.clone(base[key]))
          }
        }

        for (const wage of row.wages) {
          for (const key of Object.keys(baseWage)) {
            if (wage[key] === undefined) {
              this.$set(wage, key, baseWage[key])
            }
          }
        }
      }
    },

    /**
     * 休憩時間が無い行に、開始・終了・実働から算出した休憩時間を入れる
     * AKBS から取ってきた行（一度も保存していない・最新化で差し替えた行）には休憩時間が無いため
     * 値が入っているところは書き換えない。空でも開始・終了が無ければ算出できないのでそのまま
     */
    fillBreakHours() {
      for (const row of this.value) {
        for (const w of this.weekList) {
          if (!this.isEmpty(row['breakHours' + w.id]) || !row['start' + w.id] || !row['end' + w.id]) {
            continue
          }
          row['breakHours' + w.id] = utils.getHours(row['start' + w.id], row['end' + w.id]) - (row['workingHours' + w.id] || 0)
        }
      }
    },

    /**
     * チェックを付けられない行の「得意先へ要連絡」を外す
     * 終了日を入れてチェックしたあとに、終了日を消した場合に残らないようにする
     */
    clearContact() {
      for (const row of this.value) {
        if (row.hasContactClient && !this.canContact(row)) {
          row.hasContactClient = false
        }
      }
    },

    /**
     * 交通費が「無」で金額が空なら0を入れる
     * 入力済みの金額は上書きしない（区分と金額の不整合は保存時のチェックで拾う）
     */
    fillZeroFare() {
      for (const row of this.value) {
        for (const wage of row.wages || []) {
          for (const fare of FARE_ITEMS) {
            if (!wage[fare.typeKey] && !wage[fare.feeKey]) {
              wage[fare.feeKey] = '0'
            }
          }
        }
      }
    },

    /**
     * 新規雇用指示の行は、1件目の賃金の開始日を作業開始日に合わせる
     * 「賃金を追加」で足した2件目以降は手で入れるので触らない
     * 値が変わったときだけ書き込むので、監視が繰り返し走ることはない
     */
    fillWageStartDate() {
      for (const row of this.value) {
        if (row.sequenceNumber) {
          continue
        }
        const wage = (row.wages || [])[0]
        if (wage && wage.startDate !== row.startDateWork) {
          wage.startDate = row.startDateWork
        }
      }
    },

    /**
     * 変更前の行の終了日を、変更後の行の開始日の前日に合わせる
     * 開始日が未入力なら終了日も空に戻す
     * 値が変わったときだけ書き込むので、監視が繰り返し走ることはない
     */
    fillChangedEndDate() {
      for (const row of this.value) {
        if (!row.isChangedFrom) {
          continue
        }

        const to = this.value.find(e => e.isChangedTo && e.changeFromSequenceNumber == row.sequenceNumber)
        const endDate = to ? this.previousDay(to.startDateWork) : ''

        if (row.endDateWork !== endDate) {
          row.endDateWork = endDate
        }
      }
    },

    /**
     * 勤務の終了日より後に始まる賃金か（画面には出さない。承認時にバックが消す）
     * @param {Object} row 対象行
     * @param {Object} wage 賃金
     */
    isWageAfterEnd(row, wage) {
      return !!row.endDateWork && !!wage.startDate && wage.startDate > row.endDateWork
    },

    /**
     * 勤務の終了日の時点で効いている賃金（開始日が終了日以前の最後の1件。終了日が無ければ最後の1件）
     * @param {Object} row 対象行
     */
    effectiveLastWage(row) {
      const wages = row.wages || []
      if (!row.endDateWork) {
        return wages[wages.length - 1] || null
      }
      for (let wi = wages.length - 1; wi >= 0; wi--) {
        if (!wages[wi].startDate || wages[wi].startDate <= row.endDateWork) {
          return wages[wi]
        }
      }
      return null
    },

    /**
     * 祝日勤務の選び方（100：FULL、0：無、それ以外は「時間入力」。選択肢の名前は同じ表の「時間指定」の列と紛れないよう「時間入力」）
     * @param {Object} row 対象行
     */
    holidayKind(row) {
      const value = row.cdKubunHoliday
      // 空は「時間入力」の入力途中（時間を消して入れ直すとき）として扱い、入力欄を残す
      // 未設定の行も同じ扱いになるが、未入力の赤い背景で分かる
      if (this.isEmpty(value)) {
        return 'hours'
      }

      if (Number(value) === 100) {
        return '100'
      }

      return Number(value) === 0 ? '0' : 'hours'
    },

    /**
     * 祝日勤務の選び方を変更
     * 「時間入力」は時間数をそのまま持つため、既定の1時間を入れて入力させる
     * @param {Object} row 対象行
     * @param {String} kind 選んだ値（100／hours／0）
     */
    changeHolidayKind(row, kind) {
      row.cdKubunHoliday = kind === 'hours' ? 1 : Number(kind)
    },

    /**
     * 前日
     * @param {String} date 日付（YYYY-MM-DD）
     */
    previousDay(date) {
      return date ? moment(date).subtract(1, 'days').format('YYYY-MM-DD') : ''
    },

    /**
     * 物件1件の1週間の実働時間
     */
    weekHours(row) {
      this.reload
      return utilsAKBS.getHoursForOneWeek([row])
    },

    /**
     * 賃金1件の月額（実働時間と同じタイミングで出し直す）
     * @param row 対象の雇用指示
     * @param wage 賃金情報
     */
    monthlyWage(row, wage) {
      this.reload
      return this.formatNumber(utilsAKBS.getMonthlyWage(row, wage))
    },

    /**
     * 実働時間セット（6時間超は休憩1時間を引く）
     */
    async setTime(row, key) {
      await utils.wait(200)

      let hours = utils.getHours(row['start' + key], row['end' + key])
      if (hours > 6) {
        hours--
      }

      row['workingHours' + key] = hours
      this.getBreakHours(row, key)
    },

    /**
     * 休憩時間算出
     */
    getBreakHours(row, key) {
      row['breakHours' + key] = utils.getHours(row['start' + key], row['end' + key]) - (row['workingHours' + key] || 0)
      this.reload++
    },

    /**
     * 選べる契約コード
     * 物件を選ぶまでは契約情報が無いので空にする
     * @param row 対象の雇用指示
     */
    contractCodes(row) {
      const info = this.getSiteInfo(row.cdSite)
      if (!info || !info.contract || !info.contract.length) {
        return []
      }

      // 同じ契約コードが複数返ることがあるのでまとめる
      const codes = info.contract.map(c => c.cdContract)
      return codes.filter((c, i) => codes.indexOf(c) === i)
    },

    /**
     * 受注した契約の情報を取得
     * 物件コードをキーにした { 物件コード: { siteWage, siteShort, contract } } の形で持つ（親の val.contractList）
     */
    getContractList() {
      return this.contractList || {}
    },

    /**
     * 物件1件分の契約情報を取得（未取得なら null）
     * @param cdSite 物件コード
     */
    getSiteInfo(cdSite) {
      if (!cdSite) {
        return null
      }
      return this.getContractList()[cdSite] || null
    },

    /**
     * 契約情報が要る行か
     * 契約と突き合わせるのは今回いじる行だけ。新規追加・上書き・契約内容変更（入力できる行）と、
     * 時給+交通費を契約単価と比べる賃金のみ変更の行。触らない行や履歴の行は取らない
     * @param {Object} row 対象行
     */
    needsSiteInfo(row) {
      return this.isEditable(row) || row.mode === 'changeWage'
    },

    /**
     * 今回いじる行の物件の契約情報をまとめて取得（画面を開いたときに親から呼ぶ）
     * AKBS登録前は開くたびに今の契約情報で矛盾チェックをしたいため。登録後は控えを出すので呼ばない
     * 取得済みの物件は setSiteInfo が何もしないので、同じ物件が何行あっても通信は1回
     * 取れなかった物件があっても知らせない（開いた直後に出るダイアログは邪魔なため）。その物件は契約情報の行を出さず、
     * 修正や物件の選択をしたときにもう一度取りに行く（そのときに取れなければ setSiteInfo が知らせる）
     */
    async loadSiteInfo() {
      const rows = this.value.filter(row => this.needsSiteInfo(row))
      await Promise.all(rows.map(row => this.setSiteInfo(row.cdSite, true)))
    },

    /**
     * 契約情報を取る対象日（yyyy-mm-dd）
     * 本日と、その物件で今回いじる行の開始日の遅い方（開始日が無ければ本日）。同じ物件に複数行あれば一番遅い日
     * 契約詳細情報は契約コードごとに複数行（履歴）あるので、勤務が始まる日に効いている契約と突き合わせる（ユーザー決定 2026-09-22）
     * @param cdSite 物件コード
     */
    siteInfoDay(cdSite) {
      let day = moment().format('YYYY-MM-DD')
      for (const row of this.value) {
        if (row.cdSite != cdSite || !this.needsSiteInfo(row) || !row.startDateWork) {
          continue
        }
        const start = moment(row.startDateWork)
        if (start.isValid()) {
          const s = start.format('YYYY-MM-DD')
          if (s > day) day = s
        }
      }
      return day
    },

    /**
     * 契約情報の行を出せる物件か（取得できていて、中身が1つでもあるとき）
     * 取得中の空の入れ物や、取れたが中身が空の物件では出さない
     * @param cdSite 物件コード
     */
    hasSiteInfo(cdSite) {
      const info = this.getSiteInfo(cdSite)
      if (!info) {
        return false
      }
      return !!((info.contract && info.contract.length) || (info.siteShort && info.siteShort.date))
    },

    /**
     * 雇用指示営業所・担当営業所の候補（AREA_SELECTIONS）。今の値が候補に無い古い名前なら先頭に足して、見える・選び直せるようにする
     * @param {String} current その欄の今の値
     * @returns {Array} 候補
     */
    areaOptions(current) {
      if (current && !this.areaSelections.includes(current)) {
        return [current, ...this.areaSelections]
      }
      return this.areaSelections
    },

    /**
     * 物件の契約情報を取得して contractList に入れる
     * 同じ物件は雇用指示が何行あっても1件だけ持つ
     * 取れなかったときは空の入れ物を消して、次に修正や物件の選択をしたときにもう一度取りに行けるようにする
     * @param cdSite 物件コード
     * @param {Boolean} silent 取れなかったときに知らせないなら true（まとめて知らせる loadSiteInfo から使う）
     * @returns {Boolean} 取れた（または取る必要が無い）なら true、取れなかったら false
     */
    async setSiteInfo(cdSite, silent) {
      if (!cdSite || cdSite == '000000000') {
        return true
      }
      // 対象日（本日と、その物件の行の開始日の遅い方）。取得済みでも対象日が変わっていれば取り直す（開始日を直したとき）
      const day = this.siteInfoDay(cdSite)
      const current = this.getSiteInfo(cdSite)
      if (current && current.day === day) {
        return true
      }

      const info = {
        // 契約情報を取った対象日（契約詳細情報はこの日に効いている行で見る）
        day,
        // 契約ごとの時給単価
        siteWage: [],
        // 足りない時間（物件単位）
        siteShort: {},
        // 契約内容
        contract: []
      }
      // 物件コードは後から増えるキーなので $set で足す（通信中に同じ物件を二重に取りに行かないよう、先に入れる）
      this.$set(this.getContractList(), cdSite, info)

      let data = null
      try {
        // day：契約詳細情報は契約コードごとに複数行（履歴）あるので、対象日に効いている行で契約時間・過不足を出してもらう
        const res = await backend.searchData('akbs/getSiteWage', { siteCode: cdSite, onlyWage: false, day })
        data = res && res.data && res.data.data
      } catch (e) {
        console.log(e)
      }

      if (!data) {
        this.$delete(this.getContractList(), cdSite)
        if (!silent) {
          await dialogs.showErrorDialog('契約情報の取得に失敗しました', '契約情報が取得できませんでした。\n取得できるまで契約との突き合わせは行われません。もう一度お試しください。\n続く場合はシステムの管理者にお問い合わせください。')
        }
        return false
      }

      // 時給単価
      if (data.wage) {
        info.siteWage = data.wage.map(r => ({ cdContract: r.strRes2, wage: r.strRes3 }))
      }

      // 足りない時間（1物件1行のみ。1週間あたり＝mon〜sun・total、月＝monM〜sunM・totalM）
      if (data.short && data.short.length) {
        const s = data.short[0]
        info.siteShort = {
          total: Number(s.total),
          totalM: Number(s.totalM),
          siteName: s.nameSite,
          date: moment().format('YYYY/MM/DD HH:mm')
        }
        this.weekList.forEach(w => {
          info.siteShort[w.key] = Number(s[w.key])
          info.siteShort[w.key + 'M'] = Number(s[w.key + 'M'])
        })
        Object.keys(info.siteShort).forEach(key => this.setHoursColor(key, info.siteShort[key], info.siteShort))
      }

      // 契約内容（過不足は mon2〜sun2＝1週間あたり、monM〜sunM＝月。textMon〜textSun＝足りない週と時間の文）
      if (data.contract && data.contract.length) {
        info.contract = data.contract
        info.contract.forEach(c => {
          c.date = moment().format('YYYY/MM/DD HH:mm')
          // 過不足時間の色をセット
          this.weekList.forEach(w => {
            this.setHoursColor(w.key + '2', Number(c[w.key + '2']), c)
            this.setHoursColor(w.key + 'M', Number(c[w.key + 'M']), c)
          })
        })
      }

      this.reload++
      return true
    },

    /**
     * 過不足時間の色設定
     * @param key 対象キー
     * @param value 過不足時間
     * @param tar 色を持たせる対象
     */
    setHoursColor(key, value, tar) {
      if (value < 0) {
        this.$set(tar, key + 'Color', 'font-bold text-red-700')
      } else if (value > 0) {
        this.$set(tar, key + 'Color', 'font-bold text-blue-700')
      }
    },

    /**
     * 足りない時間
     * @param cdSite 物件コード
     * @param key siteShort のキー（mon〜sun・total）
     */
    shortHours(cdSite, key) {
      const info = this.getSiteInfo(cdSite)
      if (!info || this.isEmpty(info.siteShort[key])) {
        return 0
      }
      return info.siteShort[key]
    },

    /**
     * 過不足時間の表示
     * 不足は「-」が付くので、超過は「+」を付けて向きが分かるようにする
     * @param value 過不足時間
     */
    signedHours(value) {
      const hours = Number(value) || 0
      // 小数2桁まで（末尾の 0 は出さない）
      const text = String(parseFloat(Math.abs(hours).toFixed(2)))
      return hours > 0 ? '+' + text : (hours < 0 ? '-' + text : '0')
    },

    /**
     * 曜日ごとの不足の文のキー（mon → textMon）
     * @param week 曜日
     */
    shortTextKey(week) {
      return 'text' + week.key.charAt(0).toUpperCase() + week.key.slice(1)
    },

    /**
     * 過不足時間のクラス
     * 過不足なしは薄いグレーにして、差がある曜日に目が行くようにする
     * @param value 過不足時間
     * @param colorClass 過不足があるときの色（setHoursColor で持たせたもの）
     */
    hoursClass(value, colorClass) {
      return Number(value) ? colorClass : 'zero'
    },

    /**
     * 契約時間の表示
     * 「8:00~11:00 (3.00)＋改行＋第1,3,5週」の形で入ってくるので、改行はそのまま出す
     * @param con 契約
     * @param week 曜日
     */
    contractTime(con, week) {
      const value = con[week.key]
      return this.isEmpty(value) ? '-' : value
    },

    /**
     * 勤務コピー
     * @param row 対象データ
     * @param key 対象曜日キー
     */
    copy(row, key) {
      this.copied = {
        // 勤務形態は配列なので、コピー元・貼付先と参照を共有しないよう複製しておく
        attendance: utils.clone(row['attendance' + key] || []),
        start: row['start' + key],
        end: row['end' + key],
        workingHours: row['workingHours' + key],
        breakHours: row['breakHours' + key]
      }
    },

    /**
     * 勤務貼付け
     * @param row 対象データ
     * @param key 対象曜日キー
     */
    paste(row, key) {
      if (!this.hasCopied) {
        return
      }

      row['attendance' + key] = utils.clone(this.copied.attendance || [])
      row['start' + key] = this.copied.start
      row['end' + key] = this.copied.end
      row['workingHours' + key] = this.copied.workingHours
      row['breakHours' + key] = this.copied.breakHours
      this.reload++
    },

    /**
     * 週（月〜日の7曜日分の勤務形態・開始・終了・実働・休憩）をコピーする（入力できない行からもできる）
     * 作業内容・祝日勤務などの区分・期間・賃金は写さない
     * @param row 対象データ
     */
    copyWeek(row) {
      const week = {}
      for (const w of this.weekList) {
        week[w.id] = {
          attendance: utils.clone(row['attendance' + w.id] || []),
          start: row['start' + w.id],
          end: row['end' + w.id],
          workingHours: row['workingHours' + w.id],
          breakHours: row['breakHours' + w.id]
        }
      }
      this.copiedWeek = week
    },

    /**
     * コピーした週をこの行の7曜日へ貼り付ける（何か入っていても確認なしで上書きする。曜日の貼付と同じ）
     * @param row 対象データ
     */
    pasteWeek(row) {
      if (!this.copiedWeek) {
        return
      }
      for (const w of this.weekList) {
        const d = this.copiedWeek[w.id] || {}
        row['attendance' + w.id] = utils.clone(d.attendance || [])
        row['start' + w.id] = d.start
        row['end' + w.id] = d.end
        row['workingHours' + w.id] = d.workingHours
        row['breakHours' + w.id] = d.breakHours
      }
      this.reload++
    },

    /**
     * 物件を追加（押したらすぐ物件選択のモーダルを開く。行はモーダルで物件を選んだときに足す。やめたら何も足さない）
     */
    addRow() {
      const start = moment().format('YYYY-MM-DD')
      this.modalData = { ...createEmployment(), staffDailyCleaning: this.staffDailyCleaning, start }
      // -1 は「新しい行」の印（selectedSite で行を作る）
      this.modalIndex = -1
      this.modalOpen = true
    },

    /**
     * 修正
     * @param {Object} row 対象行
     * @param {String} type overwrite：上書き、change：契約内容変更、changeWage：賃金のみ変更
     * @param {Number} index 対象行の位置（change のときだけ使う）
     */
    async edit(row, type, index) {
      if (type == 'overwrite') {
        const res = await dialogs.showConfirmDialog('修正（上書き）でよろしいですか？', 'この契約内容を書き換えます。変更前の内容は残りません。よろしいですか？')
        if (res != 'YES') {
          return
        }
      }

      // 契約内容変更は、押した行を履歴として残したまま、同じ内容の行を1件下に足してそちらを編集する
      if (type == 'change') {
        this.change(row, index)
        this.setSiteInfo(row.cdSite)
        return
      }

      // 賃金のみ変更は、今の賃金を履歴として残したまま、同じ内容の賃金を1件足してそちらを編集する
      if (type == 'changeWage') {
        this.changeWage(row)
        this.setSiteInfo(row.cdSite)
        return
      }

      row.mode = 'edit'

      // その物件の契約情報を取得
      this.setSiteInfo(row.cdSite)
    },

    /**
     * 未保存の行を削除（何か入力されていれば確認する）
     * @param {Number} index 対象行の位置
     */
    async remove(index) {
      const row = this.value[index]
      if (this.hasInput(row)) {
        const res = await dialogs.showConfirmDialog('削除', '入力した内容が失われます。この行を削除しますか？', '削除する')
        if (res != 'YES') {
          return
        }
      }

      // 契約内容変更で足した行を取り消すときは、変更元の行も元の表示に戻す
      // 終了日は変更後の開始日から自動で入れているので、取得時の元データの値に戻す（賃金の終了日は監視で終了日に追従する）
      if (row.isChangedTo) {
        const from = this.value.find(e => e.sequenceNumber && e.sequenceNumber == row.changeFromSequenceNumber)
        if (from) {
          from.isChangedFrom = false
          const orig = this.originalRow(from)
          from.endDateWork = orig ? orig.endDateWork : ''
        }
      }

      this.value.splice(index, 1)
      this.reload++
    },

    /**
     * 行に何か入力されているか
     */
    hasInput(row) {
      if (row.site || row.cdContentsWork || row.startDateWork || row.endDateWork) {
        return true
      }

      return constantsAKBS.WEEK_LIST.some(w => (row['attendance' + w.id] || []).length)
    },

    /**
     * 契約内容変更（同じ内容の行を1件下に足し、適用期間を空にする）
     * 今回の申請で変更したことは isChangedFrom / isChangedTo で持つ
     * （changeFromSequenceNumber は過去の変更でも入っているため、表示の判定には使えない）
     */
    change(row, index) {
      const added = utils.clone(row)
      // 新しい契約として登録するため、採番は引き継がない。
      // どの契約から変更したのかは changeFromSequenceNumber に残す
      added.sequenceNumber = null
      added.changeFromSequenceNumber = row.sequenceNumber
      added.startDateWork = ''
      added.endDateWork = ''
      added.mode = 'change'
      added.isNew = true
      added.isChangedFrom = false
      added.isChangedTo = true

      // 賃金の履歴は変更前の行に残るため、引き継ぐのは今の賃金（最後の1件）だけ。
      // 勤務行の採番に紐づくので、新しい賃金として登録し直す
      const wage = added.wages[added.wages.length - 1]
      wage.id = null
      wage.isChangedFrom = false
      wage.isChangedTo = false
      added.wages = [wage]

      row.isChangedFrom = true

      this.value.splice(index + 1, 0, added)
      this.reload++

      // 足した行は画面の外にあることが多いので、開始日まで運んでカーソルを置く
      this.focusInput('startDateWork' + (index + 1))
    },

    /**
     * 賃金のみ変更（今の賃金と同じ内容を1件足し、開始日を空にする）
     * 契約内容変更と同じ考え方で、増えるのは賃金の行だけ
     * @param {Object} row 対象行
     */
    changeWage(row) {
      // 賃金を足す処理は「賃金を追加」と同じ。違いは行の状態を賃金のみ変更にすることだけ
      if (!(row.wages || []).length) {
        return
      }
      this.addWage(row)
      row.mode = 'changeWage'
    },

    /**
     * 指定の入力欄を画面の中央まで運ぶ
     * カーソルは置かない（日付欄はフォーカスするとカレンダーが開いてしまうため）
     * @param {String} key data-focus の値
     */
    focusInput(key) {
      this.$nextTick(() => {
        const el = this.$el.querySelector(`[data-focus="${key}"]`)
        if (el) {
          el.scrollIntoView({ block: 'center' })
        }
      })
    },

    /**
     * 賃金のみ変更を取消（足した賃金を全部取り除き、変更元も元の表示に戻す）
     * @param {Object} row 対象行
     */
    cancelWage(row) {
      row.wages = (row.wages || []).filter(w => !w.isChangedTo)

      for (const wage of row.wages) {
        if (wage.isChangedFrom) {
          wage.isChangedFrom = false
        }
      }

      row.mode = 'locked'
      this.reload++
    },

    /**
     * 賃金を足せる行か（賃金のみ変更・上書き・契約内容変更・新規追加）
     * @param {Object} row 対象行
     */
    canAddWage(row) {
      return row.mode === 'changeWage' || this.isEditable(row)
    },

    /**
     * 賃金を1件足す（今の最後の賃金と同じ内容で、適用日は空。最後の賃金は変更元になり、新しい適用日の前日まで効く）
     * 最低賃金が途中で上がるときに「翌日から新しい賃金」を作るためのもの。何件でも続けて足せる
     * @param {Object} row 対象行
     */
    addWage(row) {
      const wages = row.wages || []
      const last = wages[wages.length - 1]
      if (!last) {
        return
      }

      const added = utils.clone(last)
      added.id = null
      added.startDate = ''
      added.isChangedFrom = false
      added.isChangedTo = true

      last.isChangedFrom = true

      wages.push(added)
      this.reload++
      this.focusInput('wageStartDate' + this.value.indexOf(row) + '-' + (wages.length - 1))
    },

    /**
     * 足した賃金を1件取り除く（一番最後の足した賃金だけ）。1つ前の賃金は変更元でなくなる
     * 賃金のみ変更で足した賃金が無くなれば、行は未操作に戻す
     * @param {Object} row 対象行
     * @param {Number} wi 賃金の位置
     */
    removeWage(row, wi) {
      const wages = row.wages || []
      if (wi !== wages.length - 1 || !wages[wi].isChangedTo) {
        return
      }
      wages.splice(wi, 1)

      const prev = wages[wages.length - 1]
      if (prev) {
        prev.isChangedFrom = false
      }

      if (row.mode === 'changeWage' && !wages.some(w => w.isChangedTo)) {
        row.mode = 'locked'
      }
      this.reload++
    },

    /**
     * 作業終了（終了日は続けて入力してもらう。取消できるので確認は出さない）
     */
    end(row) {
      row.mode = 'ended'
      // 作業が終わることは得意先に伝える必要があるので、最初からチェックを入れる
      row.hasContactClient = true
      this.reload++
    },

    /**
     * 作業終了を取消（入力した終了日も元に戻す）
     */
    revert(row) {
      const orig = this.originalRow(row)
      row.endDateWork = orig ? orig.endDateWork : ''
      row.hasContactClient = false

      row.mode = 'locked'
      this.reload++
    },

    /**
     * 取消（追加した行なら削除、既存行なら編集前に戻す）
     */
    async cancel(row, index) {
      if (row.isNew) {
        this.value.splice(index, 1)
        this.reload++
        return
      }

      const res = await dialogs.showConfirmDialog('編集を取り消しますか？', '修正した内容が元に戻ります。\nよろしいですか？\n取り消しではなく編集終了の場合は画面右上の一時保存で保存できます。')
      if (res != 'YES') {
        return
      }

      // 変更前の内容は取得時に akbs.originalData へ控えてあるので、そこから戻す
      // （足した行は上で取り除いているため、ここへは採番のある行しか来ない）
      const orig = this.originalRow(row)
      if (orig) {
        Object.assign(row, utils.clone(orig))
      }

      row.mode = 'locked'
      this.reload++
    },

    /**
     * 取得時から値が変わったか（変わった欄は背景を黄色にする）
     * @param {Object} row 対象行
     * @param {String} key 項目のキー
     */
    isChanged(row, key) {
      const orig = this.originalRow(row)
      return !!orig && this.toText(orig[key]) !== this.toText(row[key])
    },

    /**
     * 賃金の値が取得時から変わったか
     * @param {Object} row 対象行
     * @param {Number} wi 賃金の位置
     * @param {String} key 項目のキー
     */
    isChangedWage(row, wi, key) {
      const orig = this.originalRow(row)
      if (!orig) {
        return false
      }

      const origWages = orig.wages || []
      let at = wi

      if (!row.sequenceNumber) {
        // 契約内容変更で足した行は、変更元の今の賃金（最後の1件）だけを引き継いでいる
        at = origWages.length - 1
      } else if (row.wages[wi].isChangedTo) {
        // 賃金のみ変更で足した賃金は、その1つ前（コピー元）と突き合わせる
        at = wi - 1
      }

      const before = origWages[at] || {}
      // 交通費の金額は、AKBS の NULL と画面で入れた 0（fillZeroFare）を同じに見る（空の金額を変更扱いにしない）
      if (FEE_KEYS.includes(key)) {
        return this.toFeeText(before[key]) !== this.toFeeText(row.wages[wi][key])
      }
      return this.toText(before[key]) !== this.toText(row.wages[wi][key])
    },

    /**
     * 交通費の金額を比較用の文字列にする（空・NULL は 0 と同じ）
     * @param {*} value 金額
     */
    toFeeText(value) {
      if (this.isEmpty(value)) {
        return '0'
      }
      return isNaN(Number(value)) ? String(value) : String(Number(value))
    },

    /**
     * 行の中に取得時から変わった項目があるか（賃金も含めて、背景を黄色にしている入力欄と同じものを見る）
     * @param {Object} row 対象行
     */
    isRowChanged(row) {
      const orig = this.originalRow(row)
      if (!orig) {
        return false
      }

      if (ROW_KEYS.some(key => this.isChanged(row, key))) {
        return true
      }

      for (const w of this.weekList) {
        if (DAY_KEYS.some(key => this.isChanged(row, key + w.id))) {
          return true
        }
      }

      const wages = row.wages || []
      if (wages.length !== (orig.wages || []).length) {
        return true
      }

      return wages.some((_, wi) => WAGE_KEYS.some(key => this.isChangedWage(row, wi, key)))
    },

    /**
     * 取得時から変わった行に changed を立てる（承認・AKBS登録のときに、その行を丸ごと本番へ書くため）
     * 立てる行は次の3つ。それ以外の行にはプロパティ自体を付けない。呼ぶたびに立て直す
     * ・入力欄が1つでも変わった行（賃金の変更・追加も含む）
     * ・足した行（採番が無く元データと比べられないが、本番に無いので書く必要がある）
     * ・契約内容変更の変更前の行（終了日は変更後の開始日から自動で入るため、比較に頼らず必ず書く）
     */
    markChanged() {
      for (const row of this.value) {
        delete row.changed
        delete row.workChanged

        if (!this.canCompare) {
          continue
        }

        if (!this.originalRow(row) || row.isChangedFrom || this.isRowChanged(row)) {
          row.changed = true
        }

        // 保存済みの行は、勤務の内容（備考と賃金を除く）が変わったかも控える（true／false を必ず入れる）
        // 出力画面をサブタスク「●●送付」から開いたとき、雇用契約書を出す物件・契約コードを決めるのに使う（バックの akbsOutput/taskLink）
        if (this.originalRow(row)) {
          row.workChanged = this.isWorkChanged(row)
        }
      }
    },

    /**
     * 勤務の内容が取得時から変わったか（備考と賃金は見ない。契約コード・営業所・作業内容・期間・曜日ごとの時間など）
     * @param {Object} row 対象行
     */
    isWorkChanged(row) {
      if (ROW_KEYS.some(key => key !== 'note' && this.isChanged(row, key))) {
        return true
      }
      return this.weekList.some(w => DAY_KEYS.some(key => this.isChanged(row, key + w.id)))
    },

    /**
     * 突き合わせ用の文字列（未入力は空文字、勤務形態はコードの並びに揃える）
     * @param {*} value 値
     */
    toText(value) {
      if (this.isEmpty(value)) {
        return ''
      }

      if (Array.isArray(value)) {
        return value.map(v => (v && v.id !== undefined ? v.id : v)).join(',')
      }

      return String(value)
    },

    /**
     * 変更前の雇用指示を取得（取得時に控えた akbs.originalData から探す）
     * 契約内容変更で足した行は採番が無いため、変更元の採番で引く
     * @param {Object} row 対象行
     */
    originalRow(row) {
      const sequenceNumber = row.sequenceNumber || row.changeFromSequenceNumber
      return sequenceNumber ? this.originalRows[sequenceNumber] : null
    },

    /**
     * 物件選択モーダル開く
     * @param {*} row
     * @param {Number} index 対象行の位置
     */
    openSelectSite(row, index) {
      // 契約期間絞り込み
      const start = moment().format('YYYY-MM-DD')
      this.modalData = { ...row, staffDailyCleaning: this.staffDailyCleaning, start }
      this.modalIndex = index
      this.modalOpen = true
    },

    /**
     * モーダルで物件選択
     * @param {*} site 選択した契約
     */
    selectedSite(site) {
      if (this.modalIndex === null) {
        return
      }

      // 「物件を追加」から開いたときは、ここで行を足す
      let v
      if (this.modalIndex === -1) {
        v = createEmployment()
        v.isNew = true
        // 物件が増えるので、得意先への連絡は必要な扱いで始める
        v.hasContactClient = true
        // 新規入社・再入社は開始日に入社日を入れておく（親が defaultStartDate で渡す。直せる）
        if (this.defaultStartDate) {
          v.startDateWork = this.defaultStartDate
        }
        this.value.push(v)
        this.modalIndex = this.value.length - 1
      } else {
        // 選択したデータを対象行に反映
        v = this.value[this.modalIndex]
      }

      // 物件名
      v.site = site.site1
      if (site.site2) {
        v.site += '\n' + site.site2
      }

      // 物件住所（得意先へ連絡するとき、担当エリアのスタッフをCCに入れるのに使う）
      v.siteAddress = site.siteAddress || ''

      v.cdSite = site.cdSite || site.cdSite2
      v.cdContract = site.cdContract || '00'
      v.cdContentsWork = site.cdContentsWork

      // 営業所の初期値（物件の住所の県から。代勤専門は清掃員の住所の県から。決まらなければ空のまま選ばせる）
      const judgeAddress = v.cdSite === '000000000' ? (this.staffPref || '') + (this.staffCity || '') : v.siteAddress
      v.salesOffice = utilsAKBS.salesOfficeFromAddress(judgeAddress)

      // 雇用指示営業所・担当営業所の初期値（同じ住所からエリア名を決めて、2つとも同じ値。東京都の 23 区以外は「多摩エリア」）
      // 雇用指示営業所は画面に出さないが、データとしては持つので初期値は入れる
      const area = utilsAKBS.areaFromAddress(judgeAddress)
      v.nameSalesOfficeInstructEmployment = area
      v.nameSalesOfficeCharge = area

      // 得意先（連絡先として物件名の下に出す）。物件を選び直したら連絡先も取り込み直す
      v.cdClient = site.cdClient || ''
      v.client1 = site.client1 || ''
      v.client2 = site.client2 || ''
      v.fax = site.fax || ''
      v.contactClient1 = v.client1
      v.contactClient2 = v.client2
      v.contactFax = v.fax

      // 勤務条件（祝日区分は選択肢が数値のため合わせる）
      v.cdKubunHoliday = this.isEmpty(site.kubunHolidayContract) ? '' : Number(site.kubunHolidayContract)
      v.hasCarryingoutGarbage = this.toFlag(site.hasCarryingoutGarbageContract)
      v.hasExportingCompanysHoliday = this.toFlag(site.hasExportingCompanysHolidayContract)

      // 曜日別
      for (const w of this.weekList) {
        v['attendance' + w.id] = utilsAKBS.toAttendance(site['attendance' + w.id])
        v['start' + w.id] = site['start' + w.id] || ''
        v['end' + w.id] = site['end' + w.id] || ''
        v['workingHours' + w.id] = site['workingHours' + w.id] || ''
        // 休憩は開始・終了・実働から算出する
        this.getBreakHours(v, w.id)
      }

      // 契約情報を取り込む（同じ物件が既にあれば取得済みなので何もしない）
      this.setSiteInfo(v.cdSite)

      this.reload++
    },

    /**
     * 未入力か
     * @param {*} value 判定する値
     */
    isEmpty(value) {
      return value === null || value === undefined || value === ''
    },

    /**
     * 選択肢の表示名取得
     * @param selections 選択肢
     * @param id コード
     */
    selectionName(selections, id) {
      if (this.isEmpty(id)) {
        return ''
      }
      const target = selections.find(s => String(s.id) == String(id).trim())
      return target ? target.name : id
    },

    /**
     * 区分の表示（有り／無し）
     * @param value 区分
     */
    flagText(value) {
      if (this.isEmpty(value)) {
        return '未設定'
      }
      return Number(value) === 1 ? '有り' : '無し'
    },

    /**
     * 日付の表示（YYYY/MM/DD。契約詳細入力画面と同じ。編集できない適用期間で使う）
     * @param d 'YYYY-MM-DD' など
     */
    formatDate(d) {
      if (!d) return ''
      const m = moment(String(d).slice(0, 10), 'YYYY-MM-DD')
      return m.isValid() ? m.format('YYYY/MM/DD') : String(d)
    },

    /**
     * 金額の表示（カンマ区切り）
     * @param value 金額
     */
    formatNumber(value) {
      return utils.formatNumber(value)
    },

    /**
     * 交通費の区分・金額を計算用の形にする
     * @param wage 賃金情報
     */
    fareList(wage) {
      return FARE_ITEMS.map(fare => ({ type: wage[fare.typeKey], fee: wage[fare.feeKey] }))
    },

    /**
     * 賃金1件の1か月の交通費
     * @param row 対象の雇用指示
     * @param wage 賃金情報
     */
    monthlyFare(row, wage) {
      this.reload
      return this.formatNumber(utilsAKBS.getMonthlyFare(this.fareList(wage), utilsAKBS.getWorkDaysByMonth(row)))
    },

    /**
     * 賃金1件の交通費込みの時給（保存時に契約単価と比べる値）
     * @param row 対象の雇用指示
     * @param wage 賃金情報
     */
    hourlyWage(row, wage) {
      this.reload
      return Number(wage.basicWage || 0) + utilsAKBS.getFarePerHour(row, this.fareList(wage))
    },

    /**
     * 受注している契約の単価（契約コードが一致するもの。無ければ null）
     * @param row 対象の雇用指示
     */
    contractWage(row) {
      const info = this.getSiteInfo(row.cdSite)
      if (!info || !info.siteWage || !info.siteWage.length) {
        return null
      }

      const target = info.siteWage.find(w => String(w.cdContract).trim() == String(row.cdContract).trim())
      return target && Number(target.wage) ? Number(target.wage) : null
    },

    /**
     * 契約単価を超えている分（超えていなければ0）
     * @param row 対象の雇用指示
     * @param wage 賃金情報
     */
    overWage(row, wage) {
      const limit = this.contractWage(row)
      if (!limit) {
        return 0
      }
      return Math.max(this.hourlyWage(row, wage) - limit, 0)
    },

    /**
     * 交通費が未入力か
     * 区分が「無」以外なのに金額が入っていない場合は未入力とみなす
     * @param wage 賃金
     * @param fare 交通費の項目
     */
    isFareEmpty(wage, fare) {
      return Number(wage[fare.typeKey]) > 0 && !Number(wage[fare.feeKey])
    },

    /**
     * 契約の区分を 1／0 に揃える（DBの型に合わせる）
     * @param {*} value 契約の区分
     */
    toFlag(value) {
      return value && String(value) != '0' ? 1 : 0
    }
  }
}
</script>

<style scoped>
.accordion {
  margin: 0;
  max-height: 0;
  overflow: hidden;
}

/* 開いている間は overflow を戻す（勤務形態のドロップダウンが切られないように） */
.open {
  max-height: fit-content;
  overflow: visible;
}

/* 広い画面では overflow を持たせない（持たせるとその枠が基準になって二重スクロールになり、
   見出しの上端固定も効かなくなる）。スクロールは親画面に任せ、見出しは表を抜けるまで親画面の上端に貼り付く */

/* 表が収まらない幅（表の最小幅 1704px ＋ 親画面までの余白・罫線・縦スクロールバー分 約 85px）では、
   表が枠からはみ出さないよう、表だけ横にスクロールさせる。
   このとき見出しの上端固定は効かなくなる（固定の基準がこの枠に変わるため。.toolbar の固定は効く） */
@media (max-width: 1790px) {
  .table-scroll {
    overflow-x: auto;
  }
}

/* ---- 表 ---- */
/* table-layout: fixed で colgroup の幅どおりに列を固定する（th と td がずれない） */
table.emp-table {
  table-layout: fixed;
  border-collapse: collapse;
  width: 100%;
  min-width: 1704px; /* colgroup の合計（操作96＋物件名180＋期間140＋曜日130×7＋作業内容120＋勤務条件86×3） */
}

.col-ops {
  width: 96px;
}

.col-site {
  width: 180px;
}

.col-term {
  width: 140px;
}

.col-day {
  width: 130px;
}

/* 作業内容（曜日と勤務条件の間） */
.col-work {
  width: 120px;
}

.col-attr {
  width: 86px;
}

table.emp-table th,
table.emp-table td {
  border: 1px solid #e5e7eb;
  padding: 6px;
  background: #fff;
}

table.emp-table th {
  background: #eef2ff;
  color: #312e81;
  font-weight: 700;
  text-align: center;
  padding: 8px 6px;
  border-color: #c7d2fe;
  /* 行が増えても曜日の見出しが残るように上端で固定する（上に固定した .toolbar の高さ 36px ぶん下げる） */
  position: sticky;
  top: 36px;
  z-index: 2;
}

table.emp-table th.sun {
  color: #dc2626;
}


/* 右側の列（作業内容＋勤務条件3列）を曜日から区切る */
.emp-table th.attr-start,
.emp-table td.attr-start {
  border-left: 2px solid #d1d5db;
}

.emp-table th.attr {
  background: #e0e7ff;
}

.emp-table td.attr {
  background: #fcfcfd;
}

/* ---- 操作列 ---- */
td.ops {
  padding: 8px;
}

.op-btn {
  /* アイコンと文字を横並びにするため flex */
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 26px;
  margin-bottom: 6px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  /* 入力欄は白なので、押せることが分かるよう色を敷いて影を落とす */
  background: #f3f4f6;
  color: #6b7280;
  font-size: 11px;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(17, 24, 39, 0.18);
  transition: background-color 0.15s, border-color 0.15s, box-shadow 0.15s;
}

.op-btn:last-child {
  margin-bottom: 0;
}

.op-btn:hover {
  border-color: #4338ca;
  color: #4338ca;
  box-shadow: 0 2px 5px rgba(49, 46, 129, 0.25);
}

/* 押し込んだ分だけ影を消して下げる */
.op-btn:active {
  box-shadow: none;
  transform: translateY(1px);
}

.op-btn.solid {
  background: #4338ca;
  border-color: #4338ca;
  color: #fff;
  font-weight: 700;
}

.op-btn.warn:hover {
  border-color: #dc2626;
  color: #dc2626;
}

/* 編集取消は入力欄と見分けが付くよう、塗りつぶしのボタンにする */
.op-btn.cancel {
  background: #6b7280;
  border-color: #6b7280;
  color: #fff;
  font-weight: 700;
}

.op-btn.cancel:hover {
  background: #4b5563;
  border-color: #4b5563;
  color: #fff;
}

/* 2行のボタン（見出し＋履歴の扱い）。縦に積むので高さは中身に合わせる */
.op-btn.stack {
  flex-direction: column;
  height: auto;
  padding: 5px 0;
  line-height: 1.25;
}

/* 履歴の扱いは補足なので少し小さく。1行目と詰まって見えないよう上に余白を空ける */
.op-btn .op-sub {
  margin-top: 3px;
  font-size: 10px;
}

/* 履歴が残る方が普段使う操作なので、こちらを目立たせる */
.op-btn.keep {
  background: #eef2ff;
  border-color: #4338ca;
  color: #4338ca;
  font-weight: 700;
}

/* 賃金を追加・この賃金を削除（一番下の賃金の右、駐輪の隣に縦に並べる） */
.wage-ops {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-left: 12px;
  align-self: center;
}

.op-btn.add-wage {
  padding: 2px 10px;
  font-size: 0.75rem;
  white-space: nowrap;
}

.op-btn.keep:hover {
  background: #e0e7ff;
}

/* 上書きは変更前が残らないので、あえて目立たせない。下に余白を空けて他の2つと切り離す。
   ホバーしたときだけ警告色にして、選ぼうとした時点で気付けるようにする */
.op-btn.overwrite {
  margin-bottom: 14px;
  border-color: #818cf8;
  color: #4f46e5;
}

.op-btn.overwrite:hover {
  border-color: #b45309;
  color: #b45309;
}

/* 作業終了は内容の変更ではないので、上に余白を空けて切り離す。
   補足の行が無いぶん、2行のボタンと同じ高さを確保して文字を上下中央に置く
   （内訳：上下の余白5px×2＋1行目13.75px＋間隔3px＋2行目12.5px＋枠線1px×2） */
.op-btn.finish {
  margin-top: 14px;
  min-height: 41px;
  /* 灰色だと押せないボタンに見えるので、終了の意味に合わせて薄いローズ色にする（解約済み・終了した契約の赤字と同じ系統） */
  background: #fff1f2;
  border-color: #fda4af;
  color: #be123c;
  font-weight: 700;
}

.op-btn.finish:hover {
  background: #ffe4e6;
  border-color: #e11d48;
  color: #be123c;
  box-shadow: 0 2px 5px rgba(190, 18, 60, 0.25);
}

/* ---- 表の上の操作（物件を追加・契約詳細表示） ----
   曜日の見出しと同じく、スクロールしても上端に残す。高さを固定して、見出しの固定位置（th の top）をその分下げる */
.toolbar {
  position: sticky;
  top: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 16px;
  height: 36px;
  background: #fff;
}

.toggle-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #374151;
  white-space: nowrap;
  cursor: pointer;
}

/* 契約詳細表示の切替は、契約詳細の行と同じ紫 */
.toggle-item.contract-toggle {
  color: #6b21a8;
}

/* 操作列に出す行の状態。バッジにせず文字で出し、種類は色で分ける */
.state {
  display: block;
  margin-bottom: 7px;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.3;
  text-align: center;
}

/* 補足は一段小さく（色は本文と揃える） */
.state-sub {
  display: block;
  font-size: 11px;
}

.state.edit {
  color: #92400e;
}

.state.change {
  color: #1e40af;
}

.state.before,
.state.ended {
  color: #6b7280;
}

/* 取消は無かった扱いにする操作なので赤 */
.state.cancelled {
  color: #b91c1c;
}

/* この雇用指示を取消。作業終了の下に置く。押し間違えないよう普段は白地で、ホバーで赤くする */
.op-btn.cancel-row {
  margin-top: 6px;
  min-height: 41px;
  border-color: #fca5a5;
  color: #b91c1c;
  font-weight: 700;
}

.op-btn.cancel-row:hover {
  background: #fee2e2;
  border-color: #dc2626;
  color: #b91c1c;
}

/* 新規追加は、行の緑・帯の札・契約詳細入力画面の足した行と同じ深緑 */
.state.new {
  color: #065f46;
}

/* 新規追加は見落とされると困るので、操作セルごと塗りつぶして目立たせる。
   変更あり（黄色）と同じ意味あいなので、色も同じ系統にする */
.emp-table td.ops.new {
  /* 行全体が薄い緑になったので、操作セルは少し濃い緑にして見落とされないようにする */
  background: #d1fae5;
}

/* 見出しに出す「変更あり」。入力欄の黄色と同じ系統で、そこに変更があることを示す */
.changed-mark {
  margin-left: 8px;
  padding: 1px 8px;
  border-radius: 999px;
  background: #fef9c3;
  color: #b9601e;
  font-size: 11px;
  font-weight: 700;
}

/* 取消の件数は、帯の「取消」の札と同じ赤 */
.changed-mark.cancel {
  background: #fee2e2;
  color: #b91c1c;
}

/* 賃金のみ変更の境目。行を増やさず、変更前のセルの下端から罫線にはみ出させて重ねる。
   左位置は時給の入力欄の中央（余白6px＋見出し「時給」26px＋間隔6px＋入力欄88pxの半分）。
   罫線が矢印を横切らないよう、背景を敷いて丸く抜く */
.wage-arrow {
  position: absolute;
  left: 82px;
  bottom: -12px;
  transform: translateX(-50%);
  z-index: 1;
  display: flex;
  /* padding: 1px; */
  /* border-radius: 999px; */
  /* background: #fff; */
  color: blue;
  pointer-events: none;
}

/* 行の区切り（線は引かない。見出しの帯が区切りになるので、線を引くと二重になる。少しだけ空ける） */
.row-line {
  height: 8px;
}

/* 契約内容変更で足した行が続くときは、真ん中に矢印だけ置く（線は無し） */
.row-line.changed {
  display: flex;
  align-items: center;
  justify-content: center;
  height: auto;
  padding: 4px 0;
}

/* 線と同じ色の丸に白い矢印を抜いて、面で目に入るようにする */
.change-arrow {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 999px;
  background: #1d4ed8;
  color: #fff;
}

/* ---- 行の状態 ---- */
/* 編集中の行は操作列の「編集中」バッジだけで示し、背景は他の行と同じにする */

tr.change td {
  background: #f6faff;
}

/* 今回足した行（新規追加・契約内容変更の変更後）は全体を薄い緑に（契約詳細入力画面の足した行と同じ） */
tr.new td,
tr.new td.attr {
  background: #ecfdf5;
}

/* 契約内容変更の変更前の行は全体を薄いグレーに（契約詳細入力画面の変更前の行と同じ） */
tr.before td,
tr.before td.attr {
  background: #f3f4f6;
  color: #6b7280;
}

tr.before .attendance-label {
  background: #9ca3af;
}

/* ---- 雇用指示1件の見出しの帯 ---- */
tr.seq-head td {
  padding: 5px 10px;
  border: 1px solid #c7d2fe;
  border-top: 2px solid #4338ca;
  background: #e0e7ff;
  color: #312e81;
  font-size: 12px;
  font-weight: 700;
  text-align: left;
}

/* 帯の色は行の状態に関わらず同じにする（緑・グレーの行でも帯は藍色） */
tr.seq-head.new td,
tr.seq-head.before td,
tr.seq-head.change td,
tr.seq-head.ended td,
tr.seq-head.finished td {
  background: #e0e7ff;
  color: #312e81;
}

.seq-head-code {
  margin-left: 2px;
  font-weight: 400;
  color: #4b5563;
}

.seq-head-con {
  margin-left: 14px;
}

/* 得意先名＋支店名（物件名より控えめに。契約コードの右） */
.seq-head-client {
  margin-left: 14px;
  font-size: 12px;
  font-weight: normal;
  opacity: 0.9;
}

tr.seq-head td b {
  font-size: 15px;
  letter-spacing: 0.04em;
  font-variant-numeric: tabular-nums;
}

.seq-head-tag {
  margin-left: 10px;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 10px;
}

/* 契約詳細の申請のお知らせ。札（丸い名札）と見分けが付くよう、角の四角い枠にする。作業終了がまだのときだけローズ色 */
.seq-head-notice {
  margin-left: 12px;
  padding: 1px 8px;
  border: 1px solid #d1d5db;
  border-radius: 3px;
  background: #fff;
  color: #4b5563;
  font-size: 11px;
  font-weight: normal;
}

.seq-head-notice.todo {
  border-color: #fda4af;
  background: #fff1f2;
  color: #be123c;
  font-weight: bold;
}

.seq-head-tag.new { background: #d1fae5; color: #065f46; }
.seq-head-tag.change { background: #dbeafe; color: #1e40af; }
.seq-head-tag.before { background: #e5e7eb; color: #6b7280; }
/* 取り消した雇用指示（帯に赤で出す） */
.seq-head-tag.cancelled { background: #fee2e2; color: #b91c1c; }
/* 変更なしの雇用指示（帯に灰色で出す） */
.seq-head-tag.none { background: #e5e7eb; color: #4b5563; font-size: 11px; }

/* 畳める雇用指示の帯（ほかの雇用指示に変更・追加があるときの、変更なしの行）。帯のどこを押しても開閉する */
tr.seq-head.foldable td {
  cursor: pointer;
  user-select: none;
}

tr.seq-head.foldable:hover td {
  background: #c7d2fe;
}

.seq-head-mark {
  display: inline-block;
  width: 16px;
  font-size: 10px;
}

.seq-head-fold {
  margin-left: 10px;
  color: #4338ca;
  font-size: 11px;
  text-decoration: underline;
}

/* 未保存の行は操作列の「新規」バッジだけで示し、背景は他のセルと同じにする */

/* 作業終了にした行は、続けて終了日を入力してもらうので薄くしない
   （状態は操作列の「作業終了」バッジと、この背景色で示す） */
tr.ended td {
  background: #fafbfc;
}

/* 終了日が入っている行（もう続いていない勤務）も、作業終了と同じ薄いグレーにして今の勤務と見分けられるようにする */
tr.finished td {
  background: #fafbfc;
}

/* 取り消した雇用指示：薄いグレーで、物件名・契約コードに取消線。操作列だけは戻すボタンが見えるよう色を落とさない */
tr.cancelled td,
tr.cancelled td.attr,
tr.seq-head.cancelled td {
  background: #f3f4f6;
  color: #9ca3af;
}

tr.sched.cancelled td:not(.ops) .text-indigo-900,
tr.sched.cancelled td:not(.ops) .site-name,
tr.seq-head.cancelled td {
  text-decoration: line-through;
}

tr.seq-head.cancelled .seq-head-tag {
  text-decoration: none;
}

/* 祝日勤務・ゴミ搬出・全社祝日のセルは専用の背景色を持っているため、ここで同じグレーに揃える */
tr.ended td.attr,
tr.finished td.attr {
  background: #fafbfc;
}

/* ---- セルの入力欄 ---- */
.cell-input,
.cell-select {
  height: 28px;
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 0 6px;
  font-size: 12px;
  background: #fff;
  color: #374151;
}

.cell-input.num {
  width: 76px;
  text-align: right;
}

.cell-select {
  cursor: pointer;
}

/* 入力欄の見出し */
/* 物件名の列の項目名（契約コード・最低賃金判定・営業所）。太字にして値と見分けやすくする */
.cell-cap {
  margin-bottom: 2px;
  font-size: 10px;
  font-weight: 700;
  color: #6b7280;
}

.select-site {
  min-height: 60px;
  /* 物件名を枠の中央に置く */
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  /* 押せることが分かるようボタン風にする */
  background: #eef2ff;
  border-color: #c7d2fe;
  color: #3730a3;
  box-shadow: 0 2px 4px rgba(49, 46, 129, 0.2);
  transition: background-color 0.15s, border-color 0.15s, box-shadow 0.15s;
}

.select-site:hover {
  background: #e0e7ff;
  border-color: #6366f1;
  box-shadow: 0 4px 8px rgba(49, 46, 129, 0.28);
}

.select-site:active {
  background: #c7d2fe;
  /* 押し込んだ分だけ影を浅くする */
  box-shadow: 0 1px 2px rgba(49, 46, 129, 0.2);
  transform: translateY(1px);
}

/* ロック中は入力欄を文字として見せる（行が多いときにグレーだらけで読みにくくなるため）。
   日付・時刻・金額は子コンポーネントの中に input があるので >>> で届かせる。
   子コンポーネント側が disabled のグレーを !important で当てているため、こちらも !important。
   .emp-table を重ねているのは、Suggest のグローバル CSS より詳細度を上げるため
   チェックボックス（得意先へ要連絡）は除く：枠と背景を透明にすると見えなくなる */
.emp-table.emp-table >>> td input:disabled:not([type='checkbox']),
.emp-table.emp-table >>> td select:disabled {
  background-color: transparent !important;
  border-color: transparent !important;
  color: #374151;
  -webkit-text-fill-color: #374151;
  opacity: 1;
  cursor: default;
}

/* ロック中のチェックボックスは見た目をそのままに、押せないことだけ分かるように */
.emp-table.emp-table >>> td input[type='checkbox']:disabled {
  opacity: 0.7;
  cursor: default;
}

/* 取得時から値が変わった欄は背景を黄色にする（ロック中も色は残す）。
   子コンポーネントでは外側の div に changed が付くので、中の入力欄を塗る。
   上のグレー解除（透明）と同じ詳細度にして、必ずその後ろに置くこと。
   input には上と同じ :not([type='checkbox']) を付けて詳細度をそろえる（無いと、ロック中（disabled）の入力欄は透明が勝って黄色が消える。
   承認待ちで開いた賃金のみ変更の変更後の時給・交通費が黄色にならなかった原因） */
.emp-table.emp-table >>> td input.changed:not([type='checkbox']),
.emp-table.emp-table >>> td select.changed,
.emp-table.emp-table >>> td .changed input:not([type='checkbox']),
.emp-table.emp-table >>> td .changed .multiselect__tags {
  background-color: #fef9c3 !important;
}

/* 勤務形態はロック中に文字だけになるため、そのときは文字の背景を塗る */
.attendance-label.changed-text {
  background-color: #fef9c3;
}

/* 交通費の区分はボタンなので、選んでいないボタンの側を塗って変更が分かるようにする */
.fare-kind.changed-kind .kind-btn:not(.selected) {
  background-color: #fef9c3;
}

.fare-kind.changed-kind .kind-text {
  background-color: #fef9c3;
}

/* ゴミ搬出・全社祝日出勤の有り｜無しも交通費区分と同じボタン。変更ありは選んでいない側を黄色にする */
.flag-kind.changed-kind .kind-btn:not(.selected),
.flag-kind.changed-kind .kind-text {
  background-color: #fef9c3;
}

/* 未入力（どちらも選んでいない）は、ボタンを薄い赤にして入力を促す */
.flag-kind.is-empty .kind-btn:not(.selected) {
  background-color: #fee2e2;
}

/* 未入力の欄は薄い赤で示す（ロック中も赤いままにする）。
   変更の黄色より、まず埋めてほしい赤を優先するため、後ろに置くこと */
.emp-table.emp-table >>> td input.is-empty:not([type='checkbox']),
.emp-table.emp-table >>> td select.is-empty,
.emp-table.emp-table >>> td .is-empty input:not([type='checkbox']),
.emp-table.emp-table >>> td .is-empty .multiselect__tags,
.emp-table.emp-table >>> td .office-view.is-empty {
  background-color: #fee2e2 !important;
}

/* 保存時の矛盾チェックでNGになった欄は赤い枠で示す。
   未入力の薄い赤（背景）とは別ものと分かるよう、枠にしている */
.emp-table.emp-table >>> td input.ng,
.emp-table.emp-table >>> td select.ng,
.emp-table.emp-table >>> td .ng input,
.emp-table.emp-table >>> td .ng .multiselect__tags,
.emp-table.emp-table >>> td .no-date.ng,
.emp-table.emp-table >>> td .date-view.ng,
.emp-table.emp-table >>> td .auto-end.ng,
.emp-table.emp-table >>> td div.ng .attendance-label {
  box-shadow: inset 0 0 0 2px #dc2626 !important;
}

/* 交通費の区分・有り｜無しがNGのときは、ボタンのかたまりごと赤い枠で囲む */
.emp-table.emp-table >>> td .fare-kind.ng,
.emp-table.emp-table >>> td .flag-kind.ng {
  box-shadow: inset 0 0 0 2px #dc2626;
}

/* 行全体がNG（1日も勤務日が無いなど）のときは、操作セルに赤い枠を付ける */
.sched.ng-row > td.ops {
  box-shadow: inset 0 0 0 3px #dc2626;
}

/* ---- 物件セル ---- */
/* 得意先への連絡。連絡漏れは大事故なので、枠を付けて他の項目より強く見せる */
/* 見出しの「終了した雇用指示も表示」のチェック */
.ended-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 24px;
  font-size: 12px;
  font-weight: 700;
  color: #374151;
  cursor: pointer;
  user-select: none;
}

.ended-toggle input {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

/* 承認のときは押せない（薄くする） */
.ended-toggle.disabled,
.ended-toggle.disabled input {
  color: #9ca3af;
  cursor: default;
}

/* 枠全体が label なので、枠のどこを押してもチェックが切り替わる */
.contact {
  display: block;
  margin-top: 10px;
  padding: 7px 6px;
  border: 2px solid #fdba74;
  border-radius: 4px;
  background: #fff7ed;
  color: #c2410c;
  cursor: pointer;
  user-select: none;
}

.contact:hover {
  background: #ffedd5;
}

/* まだチェックできない状態（上書きで終了日が未入力）は薄くする */
.contact.blocked {
  border-color: #e5e7eb;
  background: #f9fafb;
  color: #9ca3af;
  cursor: default;
}

.contact.blocked:hover {
  background: #f9fafb;
}

.contact-check {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

/* 連絡先はチェックの下に積む（物件名と同じく改行はそのまま出す） */
.contact-client {
  margin-top: 3px;
  text-align: center;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.3;
  color: #7c2d12;
  white-space: pre-line;
}

/* @tailwindcss/forms はチェック時の地色に input の color を使うため、そちらも指定する */
.contact input {
  width: 16px;
  height: 16px;
  color: #b9601e;
  accent-color: #b9601e;
  cursor: pointer;
}

/* 週・月額は縦に積む（幅は中身に合わせて中央に置く） */
.prop-total {
  font-size: 11px;
  color: #6b7280;
  background: #f9fafb;
  border-radius: 999px;
  padding: 2px 10px;
  display: block;
  width: fit-content;
  margin: 4px auto 0;
}

/* ---- 期間セル ---- */
.apply {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 2px;
}

.apply .lbl {
  font-size: 11px;
  color: #6b7280;
  text-align: center;
}

/* ---- 契約行（表示専用） ---- */
/* 他システムから取ってきた参照情報なので、入力欄の色（黄＝変更・赤＝未入力）と
   ぶつからない紫系にしている */
/* 物件全体の行（ユーザー指定の薄い紫） */
tr.contract td {
  background: rgba(244, 235, 247, 0.86);
  border-color: #e9d5ff;
  vertical-align: middle;
}

/* 祝日勤務・ゴミ搬出・全社祝日出勤のセルは専用の地色（.emp-table td.attr）の方が強く効くので、契約詳細の行では同じ紫に揃える */
tr.contract td.attr {
  background: rgba(244, 235, 247, 0.86);
}

/* 物件名の列は勤務行と同じく中央に揃える */
tr.contract td.head-cell {
  text-align: center;
  color: #6b21a8;
  font-weight: 700;
}

tr.contract .cap {
  display: inline-block;
  padding: 1px 7px;
  margin-right: 8px;
  border-radius: 3px;
  background: #6b1fb1;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
}

/* 取得時点・時間指定は物件名（契約コード）の下に出す */
tr.contract .fetched {
  display: block;
  margin-top: 2px;
  color: #7e22ce;
  font-size: 10px;
  font-weight: 400;
}

/* 時間指定は見落とすと困るので目立たせる */
tr.contract .fetched.strong {
  font-size: 11px;
  font-weight: 700;
}

/* 「契約詳細」は操作の列に置き、契約コード行までを縦につなげる。
   この塊の見出しなので、新規追加と同じくセルごと塗りつぶす */
tr.contract td.cap-cell {
  text-align: center;
  background: #6b1fb1;
  border-color: #6b1fb1;
}

tr.contract td.cap-cell .cap {
  margin-right: 0;
  padding: 0;
  background: none;
  font-size: 12px;
}

tr.contract .center {
  text-align: center;
  font-variant-numeric: tabular-nums;
}

tr.contract .lbl {
  font-size: 10px;
  color: #6b21a8;
}

/* 過不足なしは薄く出す */
tr.contract .zero {
  color: #6b7280;
}

/* 曜日ごとの「過不足なし」は、差がある曜日に目が行くようさらに薄くする */
tr.contract .lbl.zero {
  color: #9ca3af;
}

/* 過不足は「月（赤などの色・太字）＋週（グレー・太字）」。両方を色付きにすると赤ばかりになるので、週はグレーにする（ユーザー指示） */
tr.contract .month-main {
  color: inherit;
  font-weight: 700;
}

tr.contract .week-sub {
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  white-space: nowrap;
}

/* 足りない週と時間（「第4週 3h・第1,3,5週 1h」） */
tr.contract .short-text {
  display: inline-block;
  margin-top: 2px;
  font-size: 10px;
  font-weight: bold;
  color: #b91c1c;
  border: 1px solid #b91c1c;
  border-radius: 3px;
  padding: 0 4px;
  white-space: nowrap;
}

/* 契約コード単位の行（物件全体の行と同じ見た目で、地色だけ少し薄くする。右側3列も同じ） */
tr.contract.detail td,
tr.contract.detail td.attr {
  background: rgba(244, 235, 247, 0.5);
}

/* 契約コードはバッジ内に収めているので、右の余白は不要 */
tr.contract .cap.sub {
  background: #6b1fb1;
  margin-right: 0;
}

/* 物件全体（全契約の合計）。契約コードより濃くして上位に見せる */
tr.contract .cap.site {
  background: #581c87;
  margin-right: 0;
}

tr.contract .site-name {
  margin-top: 4px;
}

/* 契約コード行は物件全体の行にぶら下げる */
tr.contract td.head-cell.indent {
  padding-left: 22px;
}

/* ぶら下がりの線。「└」の文字は細くて見えないので、罫線で引く */
tr.contract .branch {
  display: inline-block;
  width: 10px;
  height: 9px;
  /* バッジの中心に高さを合わせる */
  margin: 0 5px 3px 0;
  border-left: 2px solid #7e22ce;
  border-bottom: 2px solid #7e22ce;
  border-bottom-left-radius: 2px;
  vertical-align: middle;
}

/* 複数の時間帯は改行で区切られてくるので、そのまま折り返す */
/* 契約時間（「7:00~15:00 (7.00)」）。紫は薄紫の背景に溶けて読みにくかったので濃いグレー（ユーザー指示） */
tr.contract .time {
  font-size: 11px;
  color: #374151;
  white-space: pre-line;
}

/* ---- 賃金行 ---- */
tr.wage td {
  background: #f8fafc;
  /* 下辺は無しにして、境目の線は次の行の上辺で決める（罫線が重なると実線＞点線＞無しの順で勝つ） */
  border-bottom: none;
}

tr.sched > td {
  border-bottom: none;
}

/* 点線は勤務行（雇用指示）と最初の賃金行の間だけ。賃金行どうし・備考行の上は普通の線 */
tr.wage.wage-first td {
  border-top: 2px dashed #c7d2fe;
}

/* 塊の一番下（区切りの行の上辺）は普通の実線。Tailwind の .border-none（border-style: none）より強くする */
tr.border-none > td.border-none {
  border-top: 1px solid #e5e7eb !important;
}

/* 賃金行は縦幅を詰める（セルの上下の余白を半分に） */
tr.wage td {
  padding-top: 3px;
  padding-bottom: 3px;
}

/* 賃金行の先頭の「└ 賃金」。勤務行にぶら下がっていることを枝線で示す（契約情報の「契約コード ○○」の行と同じ見せ方）
   賃金のみ変更の（変更前）（変更後）も同じ行に置いて、縦を取らない */
.wage-branch {
  display: flex;
  align-items: center;
  margin-bottom: 1px;
  font-size: 14px;
  font-weight: 700;
  color: #4338ca;
}

.wage-branch .wage-state {
  margin-left: 4px;
  font-size: 10px;
}

/* 賃金行の適用期間：見出しと値を横に並べて2行にする（勤務行は縦4段） */
.wage-apply {
  display: grid;
  grid-template-columns: 30px minmax(0, 1fr);
  align-items: center;
  gap: 2px 4px;
}

.wage-apply .date-cap {
  margin: 0;
  text-align: left;
  white-space: nowrap;
}

.wage-apply .date-view,
.wage-apply .no-date {
  height: 20px;
  line-height: 20px;
}

.wage-apply .auto-end {
  text-align: left;
}

/* 入力欄は幅が狭いので中の余白を詰める */
.wage-apply >>> input {
  padding-left: 4px;
  padding-right: 4px;
}

.wage-branch .branch {
  display: inline-block;
  width: 12px;
  height: 11px;
  margin: 0 5px 4px 2px;
  border-left: 2px solid #4338ca;
  border-bottom: 2px solid #4338ca;
  border-bottom-left-radius: 2px;
  vertical-align: middle;
}

/* 賃金行・備考行も、足した行は緑、変更前の行はグレー（勤務行と同じ色にして1つのかたまりに見せる） */
tr.wage.new td {
  background: #ecfdf5;
}

tr.wage.before td {
  background: #f3f4f6;
  color: #6b7280;
}

/* 備考は「ちょっとしたメモ」なので、見出しも中身も薄く小さくして主張させない */
.note-cap {
  font-size: 10px;
  letter-spacing: 0.1em;
  color: #6b7280;
}

/* 文章が入るので、見出しの隣のセルを幅いっぱいに使う */
.note-input {
  width: 100%;
  height: 26px;
  padding: 0 8px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #fff;
  /* 文字は読みやすいよう濃い灰色（2026-09-24 に #6b7280 から濃くした） */
  color: #374151;
  font-size: 11px;
  letter-spacing: 0.1em;
}

/* ロック中も入力できるときと同じ色にする（共通ルールの色に任せず、ここで揃える） */
.emp-table.emp-table >>> td input.note-input:disabled {
  color: #374151;
  -webkit-text-fill-color: #374151;
}

/* 賃金のみ変更のとき、どちらの賃金か時給の上に示す */
.wage-state {
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
}

.wage-state.before {
  color: #6b7280;
}

.wage-state.after {
  color: blue;
}

.wage-fields {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 20px;
  padding-top: 0;
}

.wf {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #6b7280;
  white-space: nowrap;
}

.wf b {
  font-weight: 700;
  color: #374151;
}

.wf .unit {
  color: #9ca3af;
}

/* 交通費1件（区分のボタンと金額を横に並べる。縦を取らない） */
.fare-body {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* 区分ボタン3つ分の幅を確保して、電車・バス・駐輪で金額の位置が揃うようにする */
.fare-body .fare-kind {
  width: 114px;
  flex: none;
}

/* 区分・有り｜無しは1かたまりの切替ボタンにする（枠線を1px重ねて連結して見せる） */
.fare-kind,
.flag-kind {
  display: flex;
  justify-content: center;
}

/* 3つとも同じ幅にして、どれが選ばれているか幅で錯覚しないようにする */
.kind-btn {
  flex: 1;
  height: 26px;
  cursor: pointer;
  margin-left: -1px;
  padding: 0 4px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #6b7280;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.kind-btn:first-child {
  margin-left: 0;
  border-radius: 4px 0 0 4px;
}

.kind-btn:last-child {
  border-radius: 0 4px 4px 0;
}

.kind-btn:hover {
  border-color: #4338ca;
  color: #4338ca;
}

/* 選んでいるものは塗りつぶす。隣に重ねた枠線に隠れないよう前面に出す */
.kind-btn.selected {
  position: relative;
  z-index: 1;
  background: #4338ca;
  border-color: #4338ca;
  color: #fff;
  font-weight: 700;
}

/* ロック中の区分。下の金額の幅に対して中央に置く */
.kind-text {
  flex: 1;
  text-align: center;
  font-size: 11px;
  font-weight: 700;
  color: #374151;
}

.fare-fee {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
}

/* 交通費（電車・バス・駐輪）。枠は付けず、間の区切り線だけで読み分ける（行の縦幅を取らないため） */
.fare-box {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 18px;
}

/* 電車・バス・駐輪を読み分けられるよう、隣り合う交通費の間に区切り線を入れる */
.fare-box .wf + .wf {
  padding-left: 18px;
  border-left: 2px solid #e5e7eb;
}

/* 時給換算・月額は行の右端に横に並べる（入る幅が無ければ次の行に右寄せで折り返す。1行分しか増えない） */
.fare-sum {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
}

/* 保存時に契約単価と比べる値 */
.hourly {
  padding: 2px 12px;
  border-radius: 999px;
  background: #eef2ff;
  color: #3730a3;
  white-space: nowrap;
}

.hourly b {
  font-weight: 700;
}

/* 契約単価を超えている（このままだと保存できない） */
.hourly.over {
  background: #fee2e2;
  color: #b91c1c;
  font-weight: 700;
}

/* 月額は横に並べる */
.sum-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.fare-total {
  padding: 2px 12px;
  border-radius: 999px;
  background: #f0fdf4;
  color: #15803d;
  font-weight: 700;
  white-space: nowrap;
}

/* NumericTextInput2 をセル内の高さ・幅に合わせる（caption は空なのでラベル行を消す） */
.wf .num-input {
  width: 80px;
}

.wage-fields >>> label {
  display: none;
}

.wage-fields >>> .mt-1 {
  margin-top: 0;
}

/* 金額の数字は薄く見えるので、濃い文字・少し太めにする */
.wage-fields >>> input {
  height: 28px;
  padding-top: 0;
  padding-bottom: 0;
  text-align: right;
  font-size: 12px;
  font-weight: 600;
  color: #111827;
  -webkit-text-fill-color: #111827;
}


/* 開始日・終了日は見出しと同じく中央に揃える */
.apply >>> input {
  text-align: center;
}

/* 祝日勤務の時間入力。列が狭いのでセレクトの下に置く */
.holiday-hours {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-top: 4px;
}

.holiday-hours input.hours-input {
  width: 48px;
  height: 26px;
  padding: 0 4px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  text-align: right;
  font-size: 12px;
  color: #111827;
  background: #fff;
}

.holiday-hours .unit {
  font-size: 10px;
  color: #9ca3af;
  white-space: nowrap;
}

/* 変更前の行の終了日。あとから自動で入ることを伝える */
.auto-end {
  text-align: center;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.35;
  color: #dc2626;
}

/* 終了日が無いことを示す。入力欄と同じ高さにして、上下の並びをずらさない */
.no-date {
  height: 28px;
  line-height: 28px;
  text-align: center;
  color: #9ca3af;
  letter-spacing: 0.1em;
}

/* 適用期間の「開始日」「終了日」の見出し（契約詳細入力画面の契約期間の見出しと同じ 10px の灰色） */
.date-cap {
  margin-bottom: -4px;
  text-align: center;
  font-size: 10px;
  font-weight: 700;
  line-height: 14px;
  color: #6b7280;
}

/* 編集できないときの日付（契約詳細入力画面の契約期間と同じ、小さい文字の YYYY/MM/DD）。
   入力欄と同じ高さにして、編集できる行と高さが変わらないようにする */
.date-view {
  height: 28px;
  line-height: 28px;
  text-align: center;
  font-size: 12px;
  color: #111827;
  border-radius: 4px;
}

.date-view.none {
  color: #9ca3af;
  letter-spacing: 0.1em;
}

/* 変更ありは入力欄と同じ黄色 */
.date-view.changed {
  background-color: #fef9c3;
}

/* ---- 曜日セル ---- */
/* コピペを常にセル下端へ置くため、中身をセルの高さいっぱいに広げる
   （td に height を与えないと、子の height:100% が解決されない） */
.emp-table td.day-cell {
  height: 1px;
}

.day {
  display: flex;
  flex-direction: column;
  gap: 6px;
  height: 100%;
}

/* 編集不可のときの勤務形態（契約詳細入力画面の変更前の行と同じ濃い緑） */
.attendance-label {
  margin: 0 auto 4px;
  padding: 2px 0;
  width: 80%;
  border-radius: 4px;
  background: #15803d;
  color: #fff;
  font-weight: 700;
  text-align: center;
}

.attendance-label:last-child {
  margin-bottom: 0;
}

/* 開始〜終了は書くときと同じ並びで横に置く */
.day .times {
  display: flex;
  align-items: center;
  gap: 2px;
}

/* 2つの入力欄で列の幅を分け合う */
.day .times > input {
  flex: 1;
  min-width: 0;
}

.day .tilde {
  flex: none;
  color: #6b7280;
  font-size: 11px;
  line-height: 1;
}

.day .work {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-size: 11px;
  color: #6b7280;
}

.day .work input {
  width: 54px;
  height: 26px;
  padding: 0 6px;
  text-align: right;
  font-size: 12px;
  font-weight: 700;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 4px;
}

/* 曜日ごとのコピペ（セル下端に寄せる） */
.day-copy {
  display: flex;
  gap: 4px;
  margin-top: auto;
  padding-top: 2px;
  /* 編集中は勤務行と賃金行の間に空きが欲しいので、コピー・貼付の下に余白を取る */
  padding-bottom: 14px;
}

/* 週（7曜日分）のコピー・貼付。物件名のセルの「週 ○ h」の下 */
.week-copy {
  display: flex;
  gap: 4px;
  margin-top: 6px;
}

.day-btn {
  flex: 1;
  padding: 2px 0;
  border: 1px solid;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  white-space: nowrap;
}

/* コピー＝取り出すだけ。落ち着いた青 */
.day-btn.copy {
  border-color: #4338ca;
  background: #eef2ff;
  color: #4338ca;
}

.day-btn.copy:hover:not(:disabled) {
  background: #c7d2fe;
}

/* 貼付＝上書きされる。押し間違い防止のため、コピーと同系色で一段濃くする */
.day-btn.paste {
  border-color: #3730a3;
  background: #c7d2fe;
  color: #312e81;
}

.day-btn.paste:hover:not(:disabled) {
  background: #a5b4fc;
}

.day-btn:disabled {
  border-color: #d1d5db;
  background: #f3f4f6;
  color: #6b7280;
  cursor: not-allowed;
}

.day .rest {
  font-size: 11px;
  color: #6b7280;
  text-align: center;
}

.day .rest b {
  font-weight: 700;
  color: #6b7280;
}

.center {
  text-align: center;
}

.text-11 {
  font-size: 11px;
}

.emp-table input[type='checkbox'] {
  width: 16px;
  height: 16px;
  cursor: pointer;
  margin-top: 4px;
}

.multiselect >>> .multiselect__tags {
  padding-right: 6px !important;
  min-height: 28px;
  padding-top: 4px;
  font-size: 12px;
  text-align: center;
}

/* 選んだ値・入力中の文字・「選ぶ」は multiselect が自分の大きさ（14px）を持つので、
   最低賃金判定のセレクト（12px）と同じ大きさに揃える */
.multiselect >>> .multiselect__single,
.multiselect >>> .multiselect__input,
.multiselect >>> .multiselect__placeholder {
  font-size: 12px;
  line-height: 18px;
  margin-bottom: 0;
  padding-top: 0;
}

/* 矢印は出さない（列が狭いので選択値の表示に幅を使う） */
.multiselect >>> .multiselect__select {
  display: none;
}
</style>
